/**
 * EntwuerfeFreigabePage — Freigabe-Queue (Issue #1071 / #14 / #15)
 *
 * v11:
 *  - Anhang-Dialog vereinheitlicht: EIN breiterer, moderner Dialog
 *    (AttachmentDialog, max-w-2xl, rounded-2xl, shadow-2xl) mit Tabs
 *    „Aus Akte" (Akten-Dokumentliste mit Suche) und „Neues Dokument" (Upload).
 *    Oben dezent das Aktenzeichen („verknüpft mit Akte …"). Upload per Drag&Drop
 *    UND Klick, Mehrfach. Checkbox „auch in die Akte ablegen" (Default an, wenn
 *    akte_id vorhanden) steuert, ob zusätzlich ein dokumente-Eintrag mit akte_id
 *    angelegt wird (→ Datei liegt in der Fallakte). Anhang immer als
 *    email_draft_attachments. Ersetzt den früheren DocPicker + Inline-Dropzone;
 *    Umbenennen/Entfernen/Aus-Akte-Auswahl bleiben erhalten.
 *  - Micro-Koop-Logo (~14px) am Marken-Badge im Gutachter-Koop-Kontext
 *    (brandLogoUrl: marken.logo_url, sonst Koop-Fallback).
 *
 * v10:
 *  - Anhang-Upload per Datei-Auswahl + Drag & Drop (UploadDropzone) neben dem
 *    bestehenden DocPicker. Datei → Storage-Bucket 'dokumente'
 *    (email-anhang/<akte_id|'ohne-akte'>/<ts>-<safeName>) → Eintrag in `dokumente`
 *    (akte_id wenn vorhanden, kategorie='email_anhang') → Eintrag in
 *    `email_draft_attachments` (source_type='supabase'). Mit akte_id liegt die
 *    Datei damit zugleich in der Akte. Mehrfach-Upload, Fortschritt/Fehler via toast.
 *  - CRM-Empfänger-Autovervollständigung: an die To/CC/BCC-Chips wird ein
 *    suggestionsProvider übergeben, der `crm_kontakte` (email/name ilike) abfragt.
 *
 * v9:
 *  - Frist & Dringlichkeit: jeder Entwurf trägt optional eine Versand-Frist
 *    (frist_versand) und eine Dringlichkeit (prioritaet). Beide werden im
 *    Bearbeiten-Modus gepflegt, als dezente Badge/Frist-Anzeige dargestellt
 *    und über eine Filter-/Sortierleiste über der Liste gefiltert/sortiert.
 *    Filter-/Sort-Logik liegt in `@/lib/freigabe-fristen` (unit-getestet).
 *  - Vorschau-iframe: sandbox="allow-same-origin" (statt komplett gesperrt),
 *    damit externe Bilder (Logo/Foto von gutachter-koop.de) sicher laden —
 *    OHNE allow-scripts, d. h. kein Skripting im Vorschau-Dokument.
 *
 * v8:
 *  - Markenwahl direkt am Absender: in der Vorschau steht im Mail-Kopf bei „Von"
 *    ein kompaktes, minimalistisches Select „Marke" + die resultierende Absender-
 *    E-Mail. Die Auswahl steuert From-Adresse, Vorschau UND Versand und wird am
 *    Entwurf persistiert (email_drafts.marken_name + from_email). Die separate
 *    Zeile „Vorschau für Marke: […]" wurde entfernt (keine doppelte Anzeige mehr).
 *
 * v7:
 *  - Markengerechte E-Mail-Vorschau: gerendertes Branded-HTML (renderBrandedEmail)
 *    in einem sandboxed <iframe> (srcDoc) — identisches Template wie der Versand.
 *
 * v6:
 *  - Adressfelder (To/CC/BCC) als echte Empfänger-Chips (RecipientChipsInput)
 *  - Save-Guard, Absender-Anzeige je Entwurf, Anhänge (Vorschau/umbenennen/entfernen)
 *  - Tabs, Suche, Info-Box, Bearbeiten/Senden/Verwerfen
 */

import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import { useLocation } from 'wouter';
import {
  Mail, Send, Trash2, RefreshCw, FileText, Loader2, Inbox, ChevronDown, ChevronRight, Clock,
  Pencil, Save, X, AlertTriangle, Info, Paperclip, Search, Plus, Download, Eye, AtSign,
  CalendarClock, ArrowDownUp, Flag, UploadCloud, Building2, Mailbox, Filter,
  Hourglass, BarChart3, PiggyBank, CheckCircle2, PauseCircle, Lock,
  ShieldCheck, ScanSearch, HelpCircle, Receipt,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import RecipientChipsInput, { parseAddresses, isValidEmail, type RecipientSuggestion } from '@/components/RecipientChipsInput';
import { renderBrandedEmail, markeToEmailBrand, type EmailBrand, type MarkeRowForBrand } from '@/lib/email-template';
import {
  fristStatus, matchesFristFilter, compareDrafts, PRIO_META,
  type FristFilter, type SortMode, type Prioritaet,
} from '@/lib/freigabe-fristen';

interface Attachment {
  id: string; source_type: string; dokument_id: string | null; storage_path: string | null;
  drive_file_id: string | null; filename: string; mime_type: string | null;
}
interface Draft {
  id: string; recipient: string; cc: string[] | null; bcc: string[] | null; subject: string;
  body_html: string | null; body_text: string | null; status: string;
  review_note: string | null; created_by_agent_id: string | null; akte_id: string | null;
  created_at: string; business_unit?: string | null; marken_name?: string | null; attachments?: Attachment[];
  frist_versand?: string | null; prioritaet?: string | null; from_email?: string | null;
  /** Aktenzeichen (akten.akten_nummer) der verknüpften Akte — nur Anzeige im Anhang-Dialog. */
  aktenzeichen?: string | null;
}
/** Vollständige Markendaten je business_unit (für Absender-Anzeige + Vorschau). */
interface MarkeInfo { name: string; email: string; color: string; brand: EmailBrand; }
interface DocRow { id: string; dateiname: string; dokument_typ: string | null; mime_type: string | null; }

// ─── Prüf-Status (View v_forderungs_versand_status) ──────────────────
/** Prüf-Stufe der Bilderkennung (Google-Vision) je Forderungs-Entwurf. */
type PruefStatus = 'geprueft' | 'in_pruefung' | 'rueckfrage';
/** Eine Zeile aus v_forderungs_versand_status (nur die hier genutzten Spalten). */
interface VersandStatusRow {
  draft_id: string;
  pruef_status: PruefStatus | null;
  befund: string | null;
  haupt_datei: string | null;
  haupt_betrag: number | string | null;
}
/** Aufbereitete Prüf-Info je Entwurf (Map draft_id → PruefInfo). */
interface PruefInfo {
  pruef_status: PruefStatus;
  befund: string | null;
  haupt_datei: string | null;
  haupt_betrag: number | string | null;
}
const PRUEF_META: Record<PruefStatus, { label: string; hint: string; badgeClass: string }> = {
  geprueft:    { label: 'Geprüft',     hint: 'geht automatisch raus', badgeClass: 'text-emerald-700 bg-emerald-50' },
  in_pruefung: { label: 'In Prüfung',  hint: 'Bilderkennung läuft',   badgeClass: 'text-gray-600 bg-gray-100' },
  rueckfrage:  { label: 'Rückfrage',   hint: 'bitte prüfen/freigeben', badgeClass: 'text-amber-800 bg-amber-50' },
};
/** Reihenfolge & Icons der Segment-Pills im Auto-Tab. */
const PRUEF_SEGMENTS: { key: PruefStatus; icon: ReactNode }[] = [
  { key: 'geprueft',    icon: <ShieldCheck className="w-3.5 h-3.5" /> },
  { key: 'in_pruefung', icon: <ScanSearch className="w-3.5 h-3.5" /> },
  { key: 'rueckfrage',  icon: <HelpCircle className="w-3.5 h-3.5" /> },
];

const BU: Record<string, { label: string; color: string }> = {
  autoglas:  { label: 'Lenneglas · Autoglas', color: '#059669' },
  mietwagen: { label: 'MietGo · Mietwagen', color: '#D97706' },
  gutachten: { label: 'Kfz Gutachter Koop', color: '#0e7490' },
  werkstatt: { label: 'LCS GmbH · Werkstatt', color: '#1F2937' },
};
const STORAGE_BUCKETS = ['akten-dokumente', 'akten-dateien', 'dokumente', 'lichtbilder'];

/** Micro-minimalistisches Koop-Logo (sehr klein) für den Gutachter-Koop-Kontext. */
const KOOP_LOGO_URL = 'https://gutachter-koop.de/downloads/logos/koop-kfz-gutachten-logo-blau.png';
/** Liefert eine kleine Logo-URL für eine Marke: marken.logo_url, sonst Koop-Fallback bei BU 'gutachten'. */
function brandLogoUrl(marke: MarkeInfo | null, businessUnit?: string | null): string | null {
  const fromMarke = marke?.brand?.logoUrl;
  if (fromMarke) return fromMarke;
  if (businessUnit === 'gutachten' || marke?.name?.toLowerCase().includes('koop')) return KOOP_LOGO_URL;
  return null;
}

const isPlaceholder = (a: string) => /BITTE|EINTRAGEN|PLATZHALTER|XXX/i.test(a || '');
const recipientInvalid = (a: string) => !isValidEmail(a) || isPlaceholder(a);

/** Dateinamen für Storage-Pfade entschärfen (keine Sonderzeichen/Pfadtrenner). */
function safeFileName(s: string): string {
  return (s || 'datei').normalize('NFKD').replace(/[^a-zA-Z0-9._-]+/g, '_').replace(/_+/g, '_').slice(0, 120) || 'datei';
}

function relTime(iso: string): string {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return 'Gerade eben';
  if (m < 60) return `vor ${m} Min`;
  if (m < 1440) return `vor ${Math.floor(m / 60)} Std`;
  if (m < 10080) return `vor ${Math.floor(m / 1440)} Tagen`;
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
function htmlToText(html: string): string {
  const t = document.createElement('div'); t.innerHTML = html;
  return (t.textContent || t.innerText || '').replace(/\n{3,}/g, '\n\n').trim();
}
function textToHtml(text: string): string {
  return text.split(/\n{2,}/).map(p =>
    `<p>${p.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>')}</p>`).join('\n');
}
/** ISO-Timestamp → Wert für <input type="datetime-local"> (lokale Zeit, ohne Sekunden/Zone). */
function toDatetimeLocal(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
/** datetime-local-Wert → ISO (oder null, wenn leer). */
function fromDatetimeLocal(v: string): string | null {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}
/** Kurze, lesbare Frist-Anzeige. */
function fristLabel(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// ─── Auto-Versand: Thema / Firma / Postfach / Versand-Plan ─────────────────
// Logik aus der früheren AutoVersandPage übernommen (Seite wird zurückgebaut).

/**
 * SICHERHEIT: Im 'auto'-Tab dürfen AUSSCHLIESSLICH echte Forderungs-Mahnungen
 * erscheinen, die zu 100 % automatisch (mit Countdown) rausgehen. Alles andere
 * (Stellungnahmen, Mietverträge/claude, Anwalt-Info, marketing, forderungs-agent,
 * null …) bleibt unter „Zur Freigabe" (manuell) und darf hier NIE auftauchen –
 * weder in der Liste, noch im Countdown, noch in irgendeinem Zähler.
 */
const AUTO_AGENTS = ['forderungs-eskalation', 'verjaehrt-anfrage'] as const;
const isAutoVersandAgent = (agentId: string | null | undefined): boolean =>
  AUTO_AGENTS.includes((agentId ?? '') as (typeof AUTO_AGENTS)[number]);

/** Thema je created_by_agent_id (inkl. künftiger Themen als Filter-Optionen). */
const THEMA_DEF: Record<string, { key: string; label: string }> = {
  'forderungs-eskalation': { key: 'forderung_mahnung', label: 'Forderung · Mahnung' },
  'verjaehrt-anfrage': { key: 'forderung_verjaehrt', label: 'Forderung · Verjährt-Anfrage' },
  'forderungs-agent': { key: 'forderung_lcs', label: 'Forderung · LCS' },
  'anwalt-infoschreiben': { key: 'anwalt_info', label: 'Anwalt-Info' },
};
/** Filter-Optionen (inkl. noch leerer künftiger Themen). */
const THEMA_FILTER: { value: string; label: string }[] = [
  { value: 'alle', label: 'Alle Themen' },
  { value: 'forderung_mahnung', label: 'Forderung · Mahnung' },
  { value: 'forderung_verjaehrt', label: 'Forderung · Verjährt-Anfrage' },
  { value: 'forderung_lcs', label: 'Forderung · LCS' },
  { value: 'anwalt_info', label: 'Anwalt-Info' },
  { value: 'marketing', label: 'Marketing (folgt)' },
  { value: 'stellungnahme', label: 'Stellungnahme (folgt)' },
  { value: 'anfrage', label: 'Anfrage (folgt)' },
  { value: 'sonstiges', label: 'Sonstiges' },
];
function themaOf(agent?: string | null): { key: string; label: string } {
  return THEMA_DEF[agent ?? ''] ?? { key: 'sonstiges', label: agent || 'Sonstiges' };
}
function firmaOf(d: Draft): 'SV Büro' | 'LCS' | 'Sonstige' {
  const s = `${d.from_email ?? ''} ${d.marken_name ?? ''}`.toLowerCase();
  if (s.includes('lenneglas') || s.includes('lcs')) return 'LCS';
  if (s.includes('gutachter-koop') || s.includes('koop')) return 'SV Büro';
  return 'Sonstige';
}
function postfachOf(d: Draft): string {
  const raw = String(d.from_email ?? '').trim();
  const m = raw.match(/<([^>]+)>/);
  return (m ? m[1] : raw).toLowerCase() || '—';
}
/** Lesbare Datum/Zeit-Anzeige für die voraussichtliche Versandzeit. */
function fmtVersandzeit(d: Date | null): string {
  if (!d) return '—';
  return d.toLocaleString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}
/** Nächster Werktags-/Geschäftszeit-Slot ab jetzt (Mo–Fr, 08:00–18:00). */
function nextBusinessStart(): Date {
  const c = new Date();
  if (c.getHours() >= 18) { c.setDate(c.getDate() + 1); c.setHours(8, 0, 0, 0); }
  else if (c.getHours() < 8) { c.setHours(8, 0, 0, 0); }
  while (c.getDay() === 0 || c.getDay() === 6) { c.setDate(c.getDate() + 1); c.setHours(8, 0, 0, 0); }
  return c;
}
/** Verteilt items über Werktage: perDay max/Tag, perSlot pro Sendetakt, stepMin Takt-Abstand. */
function planStream(items: Draft[], perDay: number, perSlot: number, stepMin: number): Map<string, Date> {
  const out = new Map<string, Date>();
  let cursor = nextBusinessStart();
  let dayCount = 0;
  for (const it of items) {
    if (dayCount >= perDay) {
      cursor = new Date(cursor); cursor.setDate(cursor.getDate() + 1); cursor.setHours(8, 0, 0, 0);
      while (cursor.getDay() === 0 || cursor.getDay() === 6) cursor.setDate(cursor.getDate() + 1);
      dayCount = 0;
    }
    const slotIndex = Math.floor(dayCount / perSlot);
    const t = new Date(cursor); t.setMinutes(t.getMinutes() + slotIndex * stepMin);
    out.set(it.id, t);
    dayCount++;
  }
  return out;
}
/** Versand-Plan über alle Streams (Mahnung / Verjährt / Rest). */
function buildVersandPlan(geplant: Draft[]): Map<string, Date> {
  const prioRank = (p?: string | null) => ({ dringend: 0, hoch: 1, mittel: 2, niedrig: 3 } as Record<string, number>)[p ?? ''] ?? 2;
  const mahn = geplant
    .filter(d => d.created_by_agent_id === 'forderungs-eskalation' || d.created_by_agent_id === 'forderungs-agent')
    .sort((a, b) => prioRank(a.prioritaet) - prioRank(b.prioritaet) || (new Date(a.created_at).getTime() - new Date(b.created_at).getTime()));
  const verj = geplant
    .filter(d => d.created_by_agent_id === 'verjaehrt-anfrage')
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  const rest = geplant.filter(d => !['forderungs-eskalation', 'forderungs-agent', 'verjaehrt-anfrage'].includes(d.created_by_agent_id ?? ''));
  const m = new Map<string, Date>();
  planStream(mahn, 30, 3, 15).forEach((v, k) => m.set(k, v));   // Mahnungen: 30/Tag, 3/Takt, 15 min
  planStream(verj, 15, 2, 30).forEach((v, k) => m.set(k, v));   // Verjährt: 15/Tag, 2/Takt, 30 min
  planStream(rest, 20, 3, 20).forEach((v, k) => m.set(k, v));   // Rest: 20/Tag, 3/Takt, 20 min
  return m;
}

/** CRM-Empfänger-Vorschläge: crm_kontakte nach name/email (ilike), nur mit email. */
async function crmRecipientSuggestions(query: string): Promise<RecipientSuggestion[]> {
  const q = query.trim();
  if (q.length < 2) return [];
  const like = `%${q}%`;
  const { data, error } = await supabase
    .from('crm_kontakte')
    .select('name, email')
    .not('email', 'is', null)
    .is('merged_into_id', null)
    .or(`email.ilike.${like},name.ilike.${like}`)
    .limit(8);
  if (error || !data) return [];
  return (data as { name: string | null; email: string | null }[])
    .filter(r => r.email)
    .map(r => ({
      label: r.name ? `${r.name} (${r.email})` : String(r.email),
      email: String(r.email),
    }));
}

/** Liefert eine anzeigbare URL für einen Anhang (signed URL aus Storage / Drive). */
async function getViewUrl(att: Attachment): Promise<string | null> {
  if (att.source_type === 'drive' && att.drive_file_id) return `https://drive.google.com/file/d/${att.drive_file_id}/view`;
  let path = att.storage_path;
  if (att.source_type === 'supabase' && att.dokument_id) {
    const { data } = await supabase.from('dokumente').select('dateipfad').eq('id', att.dokument_id).single();
    path = (data as any)?.dateipfad ?? null;
  }
  if (!path) return null;
  for (const b of STORAGE_BUCKETS) {
    const { data } = await supabase.storage.from(b).createSignedUrl(path, 600);
    if (data?.signedUrl) return data.signedUrl;
  }
  return null;
}

// ─── Dringlichkeits-Badge (dezent, koop-badge-Stil) ──────────────────────────
function PrioBadge({ prioritaet }: { prioritaet?: string | null }) {
  if (!prioritaet || !(prioritaet in PRIO_META)) return null;
  const meta = PRIO_META[prioritaet as Prioritaet];
  return (
    <span className={`koop-badge inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded ${meta.badgeClass}`}>
      <Flag className="w-2.5 h-2.5" /> {meta.label}
    </span>
  );
}

// ─── Frist-Anzeige (überfällig rot, sonst neutral) ───────────────────────────
function FristBadge({ frist }: { frist?: string | null }) {
  if (!frist) return null;
  const status = fristStatus(frist);
  const isOverdue = status === 'ueberfaellig';
  const isToday = status === 'heute';
  const cls = isOverdue
    ? 'text-red-700 bg-red-50'
    : isToday
    ? 'text-orange-700 bg-orange-50'
    : 'text-gray-500 bg-gray-100';
  const prefix = isOverdue ? 'Überfällig · ' : isToday ? 'Heute · ' : 'Frist ';
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded ${cls}`}>
      <CalendarClock className="w-2.5 h-2.5" /> {prefix}{fristLabel(frist)}
    </span>
  );
}

// ─── Prüf-Status-Badge (grün/grau/amber, koop-badge-Stil) ────────────
function PruefBadge({ status }: { status?: PruefStatus | null }) {
  if (!status || !(status in PRUEF_META)) return null;
  const meta = PRUEF_META[status];
  const icon = PRUEF_SEGMENTS.find(s => s.key === status)?.icon ?? null;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded ${meta.badgeClass}`}>
      {icon} {meta.label}
    </span>
  );
}

// ─── Lightbox ────────────────────────────────────────────────────────────────
function Lightbox({ url, filename, onClose }: { url: string; filename: string; onClose: () => void }) {
  const lower = filename.toLowerCase();
  const isImg = /\.(png|jpe?g|gif|webp|svg)$/.test(lower);
  const isFrame = /\.(pdf|html?|txt)$/.test(lower) || url.includes('drive.google.com');
  return (
    <div className="fixed inset-0 z-[120] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[88vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
          <Paperclip className="w-4 h-4 text-gray-400" />
          <span className="text-sm font-semibold text-gray-800 truncate flex-1">{filename}</span>
          <a href={url} target="_blank" rel="noreferrer" className="text-xs font-medium text-gray-500 hover:text-gray-900 flex items-center gap-1"><Download className="w-3.5 h-3.5" /> Öffnen</a>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100"><X className="w-4 h-4 text-gray-500" /></button>
        </div>
        <div className="flex-1 overflow-auto bg-gray-50 flex items-center justify-center">
          {isImg ? <img src={url} alt={filename} className="max-w-full max-h-[78vh] object-contain" />
            : isFrame ? <iframe src={url} title={filename} className="w-full h-[78vh] bg-white" />
            : <a href={url} target="_blank" rel="noreferrer" className="m-10 text-sm font-medium text-[#9B1C1C] underline">Datei öffnen / herunterladen</a>}
        </div>
      </div>
    </div>
  );
}

// ─── Anhang-Dialog (Tabs: Aus Akte / Neues Dokument) ─────────────────────────
// Vereint den früheren DocPicker (Akten-Dokumente) und den Upload (Drag&Drop +
// Klick, Mehrfach) in EINEM breiteren, modernen Dialog. Oben dezent das
// Aktenzeichen der verknüpften Akte. Beim Upload landet die Datei immer als
// E-Mail-Anhang; optional ("auch in die Akte ablegen", Default an wenn akte_id)
// zusätzlich als dokumente-Eintrag mit akte_id → liegt damit in der Fallakte.
type AttachTab = 'akte' | 'upload';

function AttachmentDialog({ draft, uploadBusy, onPick, onUpload, onClose }: {
  draft: Draft;
  uploadBusy: boolean;
  onPick: (d: DocRow) => void;
  onUpload: (files: File[], inAkte: boolean) => void;
  onClose: () => void;
}) {
  const akteId = draft.akte_id;
  const [tab, setTab] = useState<AttachTab>(akteId ? 'akte' : 'upload');
  const [docs, setDocs] = useState<DocRow[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(!!akteId);
  const [docQuery, setDocQuery] = useState('');
  const [inAkte, setInAkte] = useState<boolean>(!!akteId);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!akteId) { setLoadingDocs(false); return; }
    let alive = true;
    (async () => {
      const { data } = await supabase.from('dokumente')
        .select('id, dateiname, dokument_typ, mime_type')
        .eq('akte_id', akteId).is('geloescht_am', null)
        .order('erstellt_am', { ascending: false }).limit(100);
      if (alive) { setDocs((data as DocRow[]) ?? []); setLoadingDocs(false); }
    })();
    return () => { alive = false; };
  }, [akteId]);

  const PRIO = ['rechnung', 'mahnung', 'anschreiben', 'gutachten', 'stellungnahme', 'protokoll'];
  const sorted = useMemo(() => {
    const term = docQuery.trim().toLowerCase();
    return [...docs]
      .filter(d => !term || `${d.dateiname} ${d.dokument_typ ?? ''}`.toLowerCase().includes(term))
      .sort((a, b) => {
        const ia = PRIO.indexOf((a.dokument_typ || '').toLowerCase());
        const ib = PRIO.indexOf((b.dokument_typ || '').toLowerCase());
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      });
  }, [docs, docQuery]);

  const pick = (list: FileList | null) => {
    const files = list ? Array.from(list) : [];
    if (files.length) onUpload(files, inAkte && !!akteId);
  };

  return (
    <div className="fixed inset-0 z-[120] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[88vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Kopfzeile: Titel + Aktenverbindung + Schließen */}
        <div className="px-5 py-4 border-b border-gray-100">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#9B1C1C]/10 flex items-center justify-center flex-shrink-0">
              <Paperclip className="w-4.5 h-4.5 text-[#9B1C1C]" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-base font-bold text-gray-900">Anhang hinzufügen</h2>
              {akteId ? (
                <p className="text-[11px] text-gray-400 mt-0.5 truncate">
                  verknüpft mit Akte{draft.aktenzeichen ? <> · <span className="font-medium text-gray-500">{draft.aktenzeichen}</span></> : ''}
                </p>
              ) : (
                <p className="text-[11px] text-gray-400 mt-0.5">ohne Akte — Upload wird nur als E-Mail-Anhang gespeichert</p>
              )}
            </div>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100"><X className="w-4 h-4 text-gray-500" /></button>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 mt-3 -mb-px">
            {akteId && (
              <button
                onClick={() => setTab('akte')}
                className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${tab === 'akte' ? 'border-[#9B1C1C] text-[#9B1C1C]' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
              >
                <span className="inline-flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Aus Akte</span>
              </button>
            )}
            <button
              onClick={() => setTab('upload')}
              className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${tab === 'upload' ? 'border-[#9B1C1C] text-[#9B1C1C]' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
            >
              <span className="inline-flex items-center gap-1.5"><UploadCloud className="w-3.5 h-3.5" /> Neues Dokument</span>
            </button>
          </div>
        </div>

        {/* Inhalt */}
        <div className="flex-1 overflow-auto">
          {tab === 'akte' && akteId ? (
            <div className="p-3">
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  value={docQuery}
                  onChange={e => setDocQuery(e.target.value)}
                  placeholder="Dokument suchen…"
                  className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#9B1C1C]/20"
                />
              </div>
              {loadingDocs ? (
                <div className="p-6 text-center text-gray-400 text-sm">Lädt…</div>
              ) : sorted.length === 0 ? (
                <div className="p-6 text-center text-gray-400 text-sm">{docQuery ? 'Keine Treffer.' : 'Keine Dokumente in dieser Akte.'}</div>
              ) : (
                <div className="space-y-0.5">
                  {sorted.map(d => (
                    <button key={d.id} onClick={() => onPick(d)} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 text-left transition-colors">
                      <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-gray-800 truncate">{d.dateiname}</div>
                        {d.dokument_typ && <div className="text-[11px] text-gray-400 uppercase tracking-wide">{d.dokument_typ}</div>}
                      </div>
                      <Plus className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="p-5 space-y-3">
              {/* Hochladebereich: Drag & Drop UND Klick, Mehrfach-Upload */}
              <button
                type="button"
                onClick={() => !uploadBusy && inputRef.current?.click()}
                onDragOver={e => { e.preventDefault(); if (!uploadBusy) setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => { e.preventDefault(); setDragOver(false); if (uploadBusy) return; pick(e.dataTransfer.files); }}
                disabled={uploadBusy}
                className={`w-full flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed px-6 py-10 transition-colors disabled:opacity-60 ${
                  dragOver ? 'border-[#9B1C1C] bg-[#9B1C1C]/5' : 'border-gray-300 hover:border-gray-400 bg-gray-50/50'
                }`}
              >
                {uploadBusy ? (
                  <Loader2 className="w-7 h-7 text-[#9B1C1C] animate-spin" />
                ) : (
                  <UploadCloud className={`w-7 h-7 ${dragOver ? 'text-[#9B1C1C]' : 'text-gray-400'}`} />
                )}
                <span className="text-sm font-semibold text-gray-700">{uploadBusy ? 'Lädt hoch…' : 'Dateien hierher ziehen oder klicken'}</span>
                <span className="text-[11px] text-gray-400">PDF, Bilder, Office-Dokumente · mehrere möglich</span>
              </button>
              <input
                ref={inputRef}
                type="file"
                multiple
                accept=".pdf,image/*,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                className="hidden"
                onChange={e => { pick(e.target.files); if (e.target) e.target.value = ''; }}
              />

              {/* Auch in die Akte ablegen */}
              {akteId && (
                <label className="flex items-start gap-2.5 rounded-xl border border-gray-200 px-3 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="checkbox"
                    checked={inAkte}
                    onChange={e => setInAkte(e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded border-gray-300 text-[#9B1C1C] focus:ring-[#9B1C1C]/30"
                  />
                  <span className="text-[13px] text-gray-700">
                    <span className="font-medium">Auch in die Akte ablegen</span>
                    <span className="block text-[11px] text-gray-400">Speichert das Dokument zusätzlich in der Fallakte{draft.aktenzeichen ? ` ${draft.aktenzeichen}` : ''}.</span>
                  </span>
                </label>
              )}

              <p className="text-[11px] text-gray-400">
                Hochgeladene Dateien werden als E-Mail-Anhang hinzugefügt{akteId && inAkte ? ' und in der Akte abgelegt' : ''}.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Branded-E-Mail-Vorschau (iframe, srcDoc, sandboxed) ─────────────────────
// Die Markenwahl sitzt minimalistisch direkt im Mail-Kopf an der „Von"-Zeile:
// ein kompaktes Select + die resultierende Absender-Adresse. Sie steuert die
// Vorschau und wird am Entwurf persistiert (marken_name + from_email).
function MailPreview({ d, brandByBU, defaultBU, onBrandChange }: {
  d: Draft; brandByBU: Record<string, MarkeInfo>; defaultBU: string | null;
  onBrandChange: (markenName: string, fromEmail: string) => void;
}) {
  // Auswahl-Optionen = alle bekannten Marken (business_unit-gekeyt).
  const options = useMemo(
    () => Object.entries(brandByBU).map(([bu, info]) => ({ bu, info })),
    [brandByBU],
  );

  // Initiale Auswahl: zuvor gewählte Marke des Entwurfs (marken_name) →
  // sonst Marke der Akte (business_unit) → sonst erste verfügbare Marke.
  const buForMarkenName = useMemo(() => {
    if (!d.marken_name) return null;
    const hit = options.find(o => o.info.name === d.marken_name);
    return hit?.bu ?? null;
  }, [d.marken_name, options]);
  const initialBU =
    buForMarkenName
    ?? ((defaultBU && brandByBU[defaultBU]) ? defaultBU : (options[0]?.bu ?? null));
  const [selBU, setSelBU] = useState<string | null>(initialBU);
  useEffect(() => { setSelBU(initialBU); }, [initialBU]);

  const selected = selBU ? brandByBU[selBU] : null;
  // Fallback-Marke (Koop), falls gar keine Marken geladen wurden.
  const brand: EmailBrand = selected?.brand ?? {
    name: 'Kfz Gutachter Koop', primaryColor: '#9B1C1C',
    address: 'Rahmedestraße 65, 58507 Lüdenscheid', email: 'info@gutachter-koop.de',
    website: 'https://gutachter-koop.de',
  };

  const bodyHtml = d.body_html || textToHtml(d.body_text || '');
  const srcDoc = useMemo(
    () => renderBrandedEmail({ brand, subject: d.subject, bodyHtml }).html,
    [brand, d.subject, bodyHtml],
  );

  const fromEmail = d.from_email || brand.email || 'forderungen@gutachter-koop.de';

  const handleSelect = (bu: string) => {
    setSelBU(bu || null);
    const info = bu ? brandByBU[bu] : null;
    if (info) onBrandChange(info.name, info.email);
  };

  return (
    <div className="px-4 py-4 border-t border-gray-100 bg-gray-100">
      {/* Mail-Fenster-Rahmen: Von (mit Markenwahl) / An / CC / Betreff */}
      <div className="mx-auto max-w-[640px] bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-2.5 border-b border-gray-100 bg-gray-50 text-[12px] text-gray-600 space-y-1">
          {/* Von-Zeile: kompakte Markenwahl + resultierende Absender-Adresse */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-block w-14 text-gray-400 font-medium flex-shrink-0">Von</span>
            {options.length > 0 ? (
              <select
                value={selBU ?? ''}
                onChange={e => handleSelect(e.target.value)}
                aria-label="Absender-Marke"
                className="text-[12px] font-medium border border-gray-300 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-[#9B1C1C]/20"
                style={{ color: brand.primaryColor }}
              >
                {options.map(({ bu, info }) => (
                  <option key={bu} value={bu} style={{ color: '#1f2937' }}>{info.name}</option>
                ))}
              </select>
            ) : (
              <span className="font-medium" style={{ color: brand.primaryColor }}>{brand.name}</span>
            )}
            {fromEmail && <span className="text-gray-500 truncate">&lt;{fromEmail}&gt;</span>}
            {defaultBU && brandByBU[defaultBU] && selBU === defaultBU && (
              <span className="text-[10px] text-gray-400">(Marke der Akte)</span>
            )}
          </div>
          <div><span className="inline-block w-14 text-gray-400 font-medium">An</span>{d.recipient}</div>
          {(d.cc ?? []).length > 0 && <div><span className="inline-block w-14 text-gray-400 font-medium">CC</span>{(d.cc ?? []).join(', ')}</div>}
          <div><span className="inline-block w-14 text-gray-400 font-medium">Betreff</span><span className="font-semibold text-gray-800">{d.subject}</span></div>
        </div>
        {/*
          sandbox="allow-same-origin": erlaubt dem srcDoc-Dokument, externe
          Bilder (Logo/Foto von gutachter-koop.de) zu laden, OHNE allow-scripts —
          d. h. JavaScript bleibt im Vorschau-Dokument deaktiviert (kein
          Skripting). Das srcDoc enthält keine CSP, daher werden https-Bilder
          regulär geladen. (Mit komplett leerem sandbox="" blockten manche
          Browser-Konstellationen das Nachladen externer Ressourcen.)
        */}
        <iframe
          title={`Vorschau: ${d.subject}`}
          sandbox="allow-same-origin"
          referrerPolicy="no-referrer"
          srcDoc={srcDoc}
          className="w-full bg-white"
          style={{ height: 520, border: 0 }}
        />
      </div>
      <p className="mx-auto max-w-[640px] mt-2 text-[10px] text-gray-400 text-center">
        Die gewählte Marke bestimmt Absender, Briefkopf &amp; Fußzeile — Vorschau und Versand sind identisch.
      </p>
    </div>
  );
}

// Posteingang: gemeinsamer Mail-Typ für InboxCard + Helfer (module scope).
type InboxAttachment = { filename?: string; mimeType?: string; size?: number; attachmentId?: string };
type InboxMail = {
  id: string; gmail_message_id: string; from_email: string; from_name: string | null;
  subject: string | null; snippet: string | null; body_text: string | null; body_html: string | null;
  date: string; is_read: boolean; has_attachments: boolean; attachment_count: number | null;
  attachments: InboxAttachment[] | null;
};

// decodeMailText — body_text ist häufig Base64-codiert (z. B. "U2VociBnZWVocnRl...").
// Heuristik: nur dekodieren, wenn der getrimmte String wie Base64 aussieht; sonst Originaltext.
function decodeMailText(s: string | null): string {
  if (!s) return '';
  let t = s.replace(/\s+/g, '');
  // base64url -> base64
  t = t.replace(/-/g, '+').replace(/_/g, '/');
  // Padding ergänzen
  while (t.length % 4 !== 0) t += '=';
  if (t.length > 16 && /^[A-Za-z0-9+/]+={0,2}$/.test(t)) {
    try {
      const bin = atob(t);
      const bytes = Uint8Array.from(bin, c => c.charCodeAt(0));
      const decoded = new TextDecoder('utf-8').decode(bytes);
      if (decoded && decoded.trim().length > 0) return decoded;
    } catch { /* fallthrough */ }
  }
  return s;
}

// classifyMail — deterministische Klassifizierung (Kategorie, Farbe, Handlungsempfehlung)
// inkl. Aktenzeichen-Erkennung aus dem Betreff (Muster ACdddddd..).
function classifyMail(m: InboxMail): { label: string; tone: 'red' | 'amber' | 'green' | 'gray'; aktion: string; ak: string | null } {
  const sub = (m.subject || '').toLowerCase();
  const from = (m.from_email || '').toLowerCase();
  const ak = (m.subject || '').match(/AK\d{6,8}/i)?.[0] ?? null;
  if (from.includes('mailer-daemon') || sub.includes('delivery status notification') || sub.includes('(failure)')) return { label: 'Zustellung fehlgeschlagen (Bounce)', tone: 'red', aktion: 'Empfänger-Adresse prüfen und korrigieren', ak };
  if (from.includes('accounts.google.com') || from.includes('mail-noreply@google.com')) return { label: 'System/Google', tone: 'gray', aktion: 'Keine Aktion (Postfach-System)', ak };
  const autoFrom = /^(noreply|no-reply|no_reply|donotreply|keineantwort|nicht_antworten)/.test(from) || from.includes('donotreply') || from.includes('keineantwort') || from.includes('nicht_antworten');
  const autoSub = ['angekommen', 'empfangsbestätigung', 'empfangsbestaetigung', 'automatische antwort', 'automatic reply', 'vielen dank für ihre', 'ihre anfrage bei', 'ihr anliegen'].some(k => sub.includes(k));
  if (autoFrom || autoSub) return { label: 'Eingangsbestätigung (automatisch)', tone: 'gray', aktion: 'Keine Aktion nötig', ak };
  return { label: 'Antwort – bitte prüfen', tone: 'amber', aktion: ak ? `Akte ${ak} prüfen und zuordnen` : 'Inhalt prüfen und zuordnen', ak };
}

const TONE_BADGE: Record<'red' | 'amber' | 'green' | 'gray', string> = {
  red: 'bg-red-100 text-red-700',
  amber: 'bg-amber-100 text-amber-700',
  green: 'bg-emerald-100 text-emerald-700',
  gray: 'bg-gray-100 text-gray-600',
};

// InboxCard — Posteingang: aufklappbare Listenkarte für eine eingehende E-Mail.
// Eingeklappt: Absender/Betreff/Datum/Anhang + Klassifizierungs-Badge (+ AK-Badge).
// Aufgeklappt: „Was das System erkennt" + dekodierter Mailtext (nur Text, kein HTML).
function InboxCard({ m, open, onToggle }: { m: InboxMail; open: boolean; onToggle: () => void }) {
  const cls = classifyMail(m);
  // "In Akte übernehmen": Gmail-Anhänge dieser Mail in die erkannte Akte ablegen
  // und an offene Entwürfe (status='ready_for_review') derselben Akte hängen.
  const [uebernehmenBusy, setUebernehmenBusy] = useState(false);
  const handleUebernehmen = useCallback(async () => {
    if (!cls.ak) return;
    setUebernehmenBusy(true);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const accessToken = sess?.session?.access_token;
      if (!accessToken) { toast.error('Nicht eingeloggt'); return; }
      const res = await fetch('/api/posteingang-anhaenge-uebernehmen', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ gmail_message_id: m.gmail_message_id, akten_nr: cls.ak }),
      });
      const raw = await res.text();
      let json: any = {};
      try { json = raw ? JSON.parse(raw) : {}; } catch { /* */ }
      if (!res.ok) throw new Error(json.error || `Übernahme fehlgeschlagen (HTTP ${res.status})`);
      const n = json.uebernommen ?? 0;
      const skip = json.uebersprungen ?? 0;
      const drafts = json.an_entwuerfe_gehaengt ?? 0;
      const parts: string[] = [];
      parts.push(n === 1 ? '1 Anhang in Akte übernommen' : `${n} Anhänge in Akte übernommen`);
      if (skip) parts.push(`${skip} bereits vorhanden`);
      if (drafts) parts.push(`${drafts}× an Entwurf gehängt`);
      toast.success(`Akte ${cls.ak}: ` + parts.join(' · '));
    } catch (e: any) {
      toast.error('Übernahme fehlgeschlagen: ' + (e?.message ?? e));
    } finally {
      setUebernehmenBusy(false);
    }
  }, [cls.ak, m.gmail_message_id]);
  async function openAttachment(gmailMessageId: string, attachmentId: string, filename: string) {
    try {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      const res = await fetch(`/api/email-attachment?gmail_message_id=${encodeURIComponent(gmailMessageId)}&attachment_id=${encodeURIComponent(attachmentId)}&filename=${encodeURIComponent(filename)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) { toast.error(`Anhang konnte nicht geladen werden (${res.status})`); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error('Anhang konnte nicht geöffnet werden');
    }
  }
  const decoded = (() => {
    const t = decodeMailText(m.body_text);
    if (t.trim()) return t;
    if (m.snippet && m.snippet.trim()) return m.snippet;
    return '(kein Textinhalt)';
  })();
  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left p-4 flex items-start gap-3"
        aria-expanded={open}
      >
        <div className="w-9 h-9 rounded-full bg-[#9B1C1C]/10 flex items-center justify-center flex-shrink-0">
          <Mail className="w-4 h-4 text-[#9B1C1C]" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2">
            {!m.is_read && <span className="w-2 h-2 rounded-full bg-[#9B1C1C] flex-shrink-0 mt-1.5" aria-label="ungelesen" />}
            <p className={`text-sm truncate ${m.is_read ? 'font-medium text-gray-800' : 'font-semibold text-gray-900'}`}>{m.subject || '(kein Betreff)'}</p>
            <span className="text-[11px] text-gray-400 flex items-center gap-1 ml-auto flex-shrink-0"><Clock className="w-3 h-3" /> {relTime(m.date)}</span>
            {open ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}
          </div>
          <p className="text-[12px] text-gray-500 flex items-center gap-1 mt-0.5 truncate">
            <AtSign className="w-3 h-3 flex-shrink-0" /> Von: {m.from_name ? m.from_name + ' · ' : ''}{m.from_email}
          </p>
          <div className="flex items-center flex-wrap gap-1.5 mt-2">
            <span className={`inline-flex items-center text-[11px] font-medium rounded-full px-2 py-0.5 ${TONE_BADGE[cls.tone]}`}>{cls.label}</span>
            {cls.ak && <span className="inline-flex items-center text-[11px] font-medium rounded-full px-2 py-0.5 bg-gray-100 text-gray-600">{cls.ak}</span>}
          </div>
          {!open && m.snippet && <p className="text-[12px] text-gray-400 mt-1 line-clamp-2">{m.snippet}</p>}
          {m.has_attachments && (
            <span className="inline-flex items-center gap-1 mt-2 text-[11px] text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">
              <Paperclip className="w-3 h-3" /> {m.attachment_count || ''} Anhang
            </span>
          )}
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-0 border-t border-gray-100 mt-0">
          <div className="mt-3 rounded-lg bg-gray-50 border border-gray-200 p-3">
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Was das System erkennt</p>
            <div className="flex items-center flex-wrap gap-1.5">
              <span className={`inline-flex items-center text-[11px] font-medium rounded-full px-2 py-0.5 ${TONE_BADGE[cls.tone]}`}>{cls.label}</span>
              {cls.ak && <span className="inline-flex items-center text-[11px] font-medium rounded-full px-2 py-0.5 bg-gray-100 text-gray-600">Aktenzeichen: {cls.ak}</span>}
            </div>
            <p className="text-[12px] text-gray-600 mt-2">{cls.aktion}</p>
          </div>
          <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mt-3 mb-1">Nachricht</p>
          <div className="whitespace-pre-wrap text-sm text-gray-700 bg-white border border-gray-200 rounded-lg p-3 max-h-96 overflow-auto">{decoded}</div>
          {(() => {
            const atts = (m.attachments ?? []).filter(a => a && a.attachmentId);
            if (atts.length === 0) return null;
            return (
              <div className="mt-3">
                <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5 flex items-center gap-1">
                  <Paperclip className="w-3 h-3" /> Anhänge ({atts.length})
                </p>
                {cls.ak && (
                  <button
                    type="button"
                    onClick={handleUebernehmen}
                    disabled={uebernehmenBusy}
                    title={`Anhänge in Akte ${cls.ak} übernehmen und an offene Entwürfe hängen`}
                    className="flex items-center gap-1.5 text-xs font-semibold text-white px-3 py-1.5 rounded-lg disabled:opacity-50 mb-2"
                    style={{ background: '#9B1C1C' }}
                  >
                    {uebernehmenBusy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    <span>📎 In Akte übernehmen</span>
                  </button>
                )}
                <div className="space-y-1.5">
                  {atts.map((att, i) => {
                    const name = att.filename || 'anhang.pdf';
                    const kb = att.size ? `${Math.max(1, Math.round(att.size / 1024))} KB` : null;
                    return (
                      <button
                        key={att.attachmentId || i}
                        type="button"
                        onClick={() => openAttachment(m.gmail_message_id, att.attachmentId || '', name)}
                        className="w-full text-left flex items-center gap-2 text-sm text-[#9B1C1C] hover:underline bg-white border border-gray-200 rounded-lg px-3 py-2"
                      >
                        <Paperclip className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="truncate flex-1">{name}</span>
                        {kb && <span className="text-[11px] text-gray-400 flex-shrink-0">{kb}</span>}
                        <Download className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" />
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

function DraftCard({ d, marke, brandByBU, busy, open, onToggle, onSend, onDiscard, onSaved, onPreview, onAddAttachment, onRenameAttachment, onDeleteAttachment, onBrandChange, recipientSuggestions, versandAt, frozen, pruefInfo, onRequestEdit }: {
  d: Draft; marke: MarkeInfo | null; brandByBU: Record<string, MarkeInfo>; busy: boolean; open: boolean;
  onToggle: () => void; onSend: () => void; onDiscard: () => void; onSaved: (p: Partial<Draft>) => void;
  onPreview: (a: Attachment) => void; onAddAttachment: (d: Draft) => void;
  onRenameAttachment: (a: Attachment) => void; onDeleteAttachment: (a: Attachment) => void;
  onBrandChange: (d: Draft, markenName: string, fromEmail: string) => void;
  recipientSuggestions?: (query: string) => Promise<RecipientSuggestion[]>;
  /** Nur im Auto-Versand-Tab gesetzt: voraussichtliche Versandzeit (gedrosselt). */
  versandAt?: Date | null;
  /** Auto-Versand-Tab: eingefroren — nur Ansicht, keine Aktionen/Bearbeitung. */
  frozen?: boolean;
  /** Auto-Versand-Tab: Prüf-Info (Bilderkennung) aus v_forderungs_versand_status. */
  pruefInfo?: PruefInfo | null;
  /** Auto-Versand-Tab: „Bearbeiten" bei Rückfrage → Wechsel zum Entwürfe-Tab. */
  onRequestEdit?: (d: Draft) => void;
}) {
  const [, setLocation] = useLocation();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [recipients, setRecipients] = useState<string[]>(parseAddresses(d.recipient));
  const [cc, setCc] = useState<string[]>(d.cc ?? []);
  const [bcc, setBcc] = useState<string[]>(d.bcc ?? []);
  const [subject, setSubject] = useState(d.subject);
  const [bodyText, setBodyText] = useState(d.body_text || (d.body_html ? htmlToText(d.body_html) : ''));
  const [fristVersand, setFristVersand] = useState<string>(toDatetimeLocal(d.frist_versand));
  const [prioritaet, setPrioritaet] = useState<string>(d.prioritaet ?? '');

  const invalid = recipientInvalid(d.recipient);
  const isReady = d.status === 'ready_for_review';
  const isSent = d.status === 'sent';
  const noAttachments = (d.attachments ?? []).length === 0;
  const bu = d.business_unit ? BU[d.business_unit] : null;
  // Anzeige-Marke: explizit gewählte (marken_name) gewinnt über business_unit-Marke.
  const chosenMarke = d.marken_name
    ? Object.values(brandByBU).find(m => m.name === d.marken_name) ?? null
    : null;
  const effMarke = chosenMarke ?? marke;
  const accent = effMarke?.color ?? bu?.color ?? '#9B1C1C';

  // Auto-Versand-Prüfstufen: Countdown nur bei „geprüft", Aktionen nur bei „Rückfrage".
  const pruefStatus = pruefInfo?.pruef_status ?? null;
  const showCountdown = !pruefStatus || pruefStatus === 'geprueft';
  const isRueckfrage = frozen && pruefStatus === 'rueckfrage';
  const hauptBetragNum = pruefInfo && pruefInfo.haupt_betrag != null ? Number(pruefInfo.haupt_betrag) : null;

  // Save-Guard: mind. eine gültige To-Adresse, keine ungültige in To/CC/BCC.
  const hasValidTo = recipients.some(a => isValidEmail(a));
  const anyInvalid = [...recipients, ...cc, ...bcc].some(a => !isValidEmail(a));
  const canSave = hasValidTo && !anyInvalid;
  const saveHint = !hasValidTo
    ? 'Mindestens eine gültige Empfänger-Adresse erforderlich.'
    : anyInvalid
    ? 'Bitte ungültige Adressen (rot) korrigieren oder entfernen.'
    : '';

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const patch: Partial<Draft> = {
        recipient: recipients.join(', '),
        subject: subject.trim(),
        cc: cc.length ? cc : null,
        bcc: bcc.length ? bcc : null,
        body_text: bodyText,
        body_html: textToHtml(bodyText),
        frist_versand: fromDatetimeLocal(fristVersand),
        prioritaet: prioritaet || null,
      };
      const { error } = await supabase.from('email_drafts').update(patch).eq('id', d.id);
      if (error) throw error;
      toast.success('Entwurf gespeichert'); onSaved(patch); setEditing(false);
    } catch (e: any) { toast.error('Speichern fehlgeschlagen: ' + (e?.message ?? e)); }
    finally { setSaving(false); }
  };

  return (
    <div className={`rounded-xl border bg-white overflow-hidden ${invalid && !isSent ? 'border-amber-300' : 'border-gray-200'}`}>
      <div className="flex items-start gap-3 px-4 py-3">
        <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: `${accent}1A` }}>
          <Mail className="w-4.5 h-4.5" style={{ color: accent }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${isSent ? 'bg-blue-100 text-blue-700' : isReady ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
              {isSent ? 'Gesendet' : isReady ? 'Zur Freigabe' : 'Entwurf'}
            </span>
            {bu && (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full text-white" style={{ background: bu.color }}>
                {(() => { const logo = brandLogoUrl(effMarke, d.business_unit); return logo ? <img src={logo} alt="" className="h-3.5 w-auto rounded-sm bg-white/90 p-px object-contain" loading="lazy" /> : null; })()}
                {bu.label}
              </span>
            )}
            <PrioBadge prioritaet={d.prioritaet} />
            {pruefStatus && <PruefBadge status={pruefStatus} />}
            {!isSent && <FristBadge frist={d.frist_versand} />}
            {d.created_by_agent_id && <span className="text-[10px] text-gray-400">{d.created_by_agent_id}</span>}
            <span className="text-[11px] text-gray-400 flex items-center gap-1 ml-auto flex-shrink-0"><Clock className="w-3 h-3" /> {relTime(d.created_at)}</span>
          </div>

          {!editing ? (
            <>
              <p className="text-sm font-semibold text-gray-900 mt-1">{d.subject}</p>
              <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5 flex-wrap">
                <span className="inline-flex items-center gap-1 font-medium" style={{ color: accent }}>
                  <AtSign className="w-3 h-3" /> Von: {d.marken_name || effMarke?.name || 'Kfz-Sachverständigenbüro Koop – Forderungen'} · {d.from_email || 'forderungen@gutachter-koop.de'}
                </span>
                <span>· an {d.recipient}</span>
                {(d.cc ?? []).length > 0 && <span>· CC: {(d.cc ?? []).join(', ')}</span>}
                {(d.bcc ?? []).length > 0 && <span>· BCC: {(d.bcc ?? []).join(', ')}</span>}
              </div>
              {/* Auto-Versand: Prüf-Befund + (bei „geprüft") Versandzeit/Countdown + Thema/Firma/Postfach */}
              {versandAt !== undefined && (
                <div className="flex items-center gap-1.5 mt-1.5 flex-wrap text-[11px]">
                  {showCountdown && (
                    <>
                      <span className="inline-flex items-center gap-1 font-medium text-amber-700 bg-amber-50 rounded px-1.5 py-0.5">
                        <Clock className="w-3 h-3" /> voraussichtlich: {fmtVersandzeit(versandAt)}
                      </span>
                      <CountdownBadge versandAt={versandAt ?? null} />
                    </>
                  )}
                  <span className="inline-flex items-center gap-1 text-gray-600 bg-gray-100 rounded-full px-1.5 py-0.5">
                    {themaOf(d.created_by_agent_id).label}
                  </span>
                  <span className="inline-flex items-center gap-1 text-gray-600 bg-gray-100 rounded-full px-1.5 py-0.5">
                    <Building2 className="w-2.5 h-2.5" /> {firmaOf(d)}
                  </span>
                  <span className="inline-flex items-center gap-1 text-gray-500 bg-gray-100 rounded-full px-1.5 py-0.5">
                    <Mailbox className="w-2.5 h-2.5" /> {postfachOf(d)}
                  </span>
                </div>
              )}
              {/* Auto-Versand: Befund der Bilderkennung + erkannte Rechnung/Betrag */}
              {pruefStatus && (pruefInfo?.befund || pruefInfo?.haupt_datei) && (
                <div className="mt-1.5 space-y-1">
                  {pruefInfo?.befund && (
                    <p className={`text-[11px] flex items-start gap-1 rounded px-2 py-1 ${PRUEF_META[pruefStatus].badgeClass}`}>
                      {pruefStatus === 'rueckfrage'
                        ? <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
                        : pruefStatus === 'in_pruefung'
                        ? <ScanSearch className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
                        : <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0 mt-px" />}
                      <span>{pruefInfo.befund}</span>
                    </p>
                  )}
                  {pruefInfo?.haupt_datei && (
                    <p className="text-[11px] text-gray-500 flex items-center gap-1">
                      <Receipt className="w-3.5 h-3.5 text-gray-400" />
                      Rechnung: <span className="font-medium text-gray-700 truncate">{pruefInfo.haupt_datei}</span>
                      {hauptBetragNum != null && <span className="text-gray-500"> · erkannt {eur(hauptBetragNum)}</span>}
                    </p>
                  )}
                </div>
              )}
              {/* Anhänge — Vorschau · umbenennen · entfernen + Anhang hinzufügen (Dialog) */}
              <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                {(d.attachments ?? []).map(a => (
                  <span key={a.id} className="inline-flex items-center gap-0.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded px-1.5 py-0.5 text-[11px] transition-colors">
                    <button onClick={() => onPreview(a)} title="Vorschau" className="inline-flex items-center gap-1 max-w-[220px] truncate">
                      <Eye className="w-3 h-3 flex-shrink-0" /><span className="truncate">{a.filename}</span>
                    </button>
                    {!isSent && !frozen && (
                      <>
                        <button onClick={() => onRenameAttachment(a)} title="Umbenennen" className="ml-1 opacity-50 hover:opacity-100"><Pencil className="w-3 h-3" /></button>
                        <button onClick={() => onDeleteAttachment(a)} title="Entfernen" className="opacity-50 hover:opacity-100 hover:text-red-600"><X className="w-3 h-3" /></button>
                      </>
                    )}
                  </span>
                ))}
                {!isSent && !frozen && (
                  <button onClick={() => onAddAttachment(d)} className="inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-gray-900 border border-dashed border-gray-300 rounded px-1.5 py-0.5 transition-colors">
                    <Plus className="w-3 h-3" /> Anhang hinzufügen
                  </button>
                )}
              </div>
              {!isSent && !frozen && noAttachments && (
                <p className="text-[11px] text-amber-600 mt-1.5 flex items-center gap-1 bg-amber-50 rounded px-2 py-1">
                  <Paperclip className="w-3.5 h-3.5" /> Kein Anhang — ggf. Rechnung / Mahnung / Protokoll anhängen.
                </p>
              )}
              {invalid && !isSent && (
                <p className="text-[11px] text-amber-700 mt-1.5 flex items-center gap-1 bg-amber-50 rounded px-2 py-1">
                  <AlertTriangle className="w-3.5 h-3.5" /> Empfänger ungültig/Platzhalter — vor dem Senden bearbeiten.
                </p>
              )}
            </>
          ) : (
            <div className="mt-2 space-y-2">
              <RecipientChipsInput id={`to-${d.id}`} label="Empfänger" value={recipients} onChange={setRecipients} placeholder="Adresse eingeben und Enter" suggestionsProvider={recipientSuggestions} />
              <RecipientChipsInput id={`cc-${d.id}`} label="CC" value={cc} onChange={setCc} placeholder="Adresse eingeben und Enter" suggestionsProvider={recipientSuggestions} />
              <RecipientChipsInput id={`bcc-${d.id}`} label="BCC" value={bcc} onChange={setBcc} placeholder="Blindkopie — für andere Empfänger nicht sichtbar" suggestionsProvider={recipientSuggestions} />
              <label className="block text-[11px] font-medium text-gray-500">Betreff
                <input value={subject} onChange={e => setSubject(e.target.value)} className="mt-0.5 w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2" />
              </label>
              {/* Frist + Dringlichkeit nebeneinander */}
              <div className="grid grid-cols-2 gap-2">
                <label className="block text-[11px] font-medium text-gray-500">Frist zum Versenden
                  <input
                    type="datetime-local"
                    value={fristVersand}
                    onChange={e => setFristVersand(e.target.value)}
                    className="mt-0.5 w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2"
                  />
                </label>
                <label className="block text-[11px] font-medium text-gray-500">Dringlichkeit
                  <select
                    value={prioritaet}
                    onChange={e => setPrioritaet(e.target.value)}
                    className="mt-0.5 w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white focus:outline-none focus:ring-2"
                  >
                    <option value="">— keine —</option>
                    <option value="niedrig">Niedrig</option>
                    <option value="mittel">Mittel</option>
                    <option value="hoch">Hoch</option>
                    <option value="dringend">Dringend</option>
                  </select>
                </label>
              </div>
              <label className="block text-[11px] font-medium text-gray-500">Text
                <textarea value={bodyText} onChange={e => setBodyText(e.target.value)} rows={10} className="mt-0.5 w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 font-sans leading-relaxed focus:outline-none focus:ring-2" />
              </label>
              {!canSave && (
                <p className="text-[11px] text-amber-700 flex items-center gap-1 bg-amber-50 rounded px-2 py-1">
                  <AlertTriangle className="w-3.5 h-3.5" /> {saveHint}
                </p>
              )}
              <p className="text-[10px] text-gray-400">Hinweis: Briefkopf/Signatur (Marke) werden beim Versand automatisch angefügt — wie in der Vorschau.</p>
            </div>
          )}
        </div>
      </div>

      {!editing && (d.review_note || d.akte_id) && (
        <div className="px-4">
          <button onClick={() => setShowInfo(v => !v)} className="w-full flex items-center gap-1.5 text-[11px] font-medium text-gray-500 hover:text-gray-800 py-1.5">
            <Info className="w-3.5 h-3.5" style={{ color: accent }} /> Warum diese E-Mail? (Kontext)
            <ChevronDown className={`w-3 h-3 transition-transform ${showInfo ? 'rotate-180' : ''}`} />
          </button>
          {showInfo && (
            <div className="mb-2 rounded-lg bg-gray-50 border border-gray-100 px-3 py-2 text-[12px] text-gray-700 space-y-1">
              {d.review_note && <div><span className="font-semibold text-gray-500">Anlass/Ziel:</span> {d.review_note}</div>}
              {d.created_by_agent_id && <div><span className="font-semibold text-gray-500">Quelle:</span> {d.created_by_agent_id}</div>}
              {d.akte_id && <div><span className="font-semibold text-gray-500">Verknüpfte Akte:</span>{' '}<button onClick={() => setLocation('/forderungen')} className="font-medium underline" style={{ color: accent }}>Akte öffnen</button></div>}
            </div>
          )}
        </div>
      )}

      {!editing && (
        <button onClick={onToggle} className="w-full flex items-center justify-center gap-1 text-[11px] font-medium text-gray-400 hover:text-gray-700 py-1 border-t border-gray-100">
          {frozen ? <Lock className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />} {open ? (frozen ? 'Inhalt ausblenden' : 'Vorschau ausblenden') : (frozen ? 'Inhalt ansehen' : 'Vorschau anzeigen (so kommt die Mail an)')}
          <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      )}

      {open && !editing && (
        frozen ? (
          <FrozenMailPreview d={d} />
        ) : (
          <MailPreview
            d={d}
            brandByBU={brandByBU}
            defaultBU={d.business_unit ?? null}
            onBrandChange={(markenName, fromEmail) => onBrandChange(d, markenName, fromEmail)}
          />
        )
      )}

      {!isSent && !frozen && (
        <div className="flex items-center gap-2 px-4 py-2 border-t border-gray-100 bg-gray-50/50">
          {editing ? (
            <>
              <button onClick={save} disabled={saving || !canSave} title={canSave ? 'Speichern' : saveHint}
                className="flex items-center gap-1.5 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 px-3 py-1.5 rounded-lg disabled:opacity-60">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Speichern
              </button>
              <button onClick={() => setEditing(false)} disabled={saving} className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-800 px-3 py-1.5 rounded-lg hover:bg-gray-100"><X className="w-3.5 h-3.5" /> Abbrechen</button>
            </>
          ) : (
            <>
              <button onClick={onSend} disabled={busy || invalid} title={invalid ? 'Empfänger zuerst korrigieren' : 'Freigeben & Senden'}
                className="flex items-center gap-1.5 text-xs font-semibold text-white px-3 py-1.5 rounded-lg disabled:opacity-50" style={{ background: invalid ? '#9ca3af' : accent }}>
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Freigeben & Senden
              </button>
              <button onClick={() => setEditing(true)} disabled={busy} className="flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 px-3 py-1.5 rounded-lg hover:bg-gray-100"><Pencil className="w-3.5 h-3.5" /> Bearbeiten</button>
              <button onClick={onDiscard} disabled={busy} className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-red-600 px-3 py-1.5 rounded-lg hover:bg-red-50 ml-auto"><Trash2 className="w-3.5 h-3.5" /> Verwerfen</button>
            </>
          )}
        </div>
      )}

      {/* Auto-Versand „Rückfrage": eingefroren, aber manuell freigebbar/bearbeitbar */}
      {isRueckfrage && (
        <div className="flex items-center gap-2 px-4 py-2 border-t border-gray-100 bg-amber-50/50">
          <button onClick={onSend} disabled={busy || invalid} title={invalid ? 'Empfänger zuerst korrigieren' : 'Freigeben & senden'}
            className="flex items-center gap-1.5 text-xs font-semibold text-white px-3 py-1.5 rounded-lg disabled:opacity-50" style={{ background: invalid ? '#9ca3af' : '#b45309' }}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Freigeben &amp; senden
          </button>
          <button onClick={() => onRequestEdit?.(d)} disabled={busy} className="flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 px-3 py-1.5 rounded-lg hover:bg-gray-100"><Pencil className="w-3.5 h-3.5" /> Bearbeiten</button>
        </div>
      )}
    </div>
  );
}

// ─── Auto-Versand: Erfolgs-/Pipeline-Kennzahlen (View v_forderungs_versand_kennzahlen) ──
/**
 * Eine Zeile aus v_forderungs_versand_kennzahlen. Numerische Spalten können als
 * Postgres-`numeric` ankommen → kommen über supabase-js als STRING. Deshalb hier
 * bewusst `number | string` und beim Rendern immer mit `Number(...)` parsen.
 */
interface VersandKennzahlen {
  geplant_anzahl: number | string;
  geplant_offen: number | string;
  versendet_anzahl: number | string;
  versendet_offen: number | string;
  zurueckgehalten_anzahl: number | string;
  offen_gesamt: number | string;
  eingegangen_gesamt: number | string;
}

/** Beträge im deutschen EUR-Format (robust ggü. String/numeric/null). */
function eur(n: unknown): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(n) || 0);
}

/** Ganzzahl-Anzeige (robust ggü. String/numeric/null), de-DE gruppiert. */
function intDe(n: unknown): string {
  return new Intl.NumberFormat('de-DE').format(Math.round(Number(n) || 0));
}

/**
 * Eine KPI-Kachel im ruhigen, hochwertigen SaaS-Stil: weiße Karte, weicher
 * Schatten, neutrale Typografie. Farbe sehr sparsam – nur der Wert darf via
 * `success` dezent grün sein; Icons bleiben grundsätzlich dezent grau.
 */
function KpiCard({ icon, label, wert, sub, success, info }: {
  icon: ReactNode; label: string; wert: string; sub?: string; success?: boolean; info?: string;
}) {
  return (
    <div className="flex h-full min-w-0 flex-col rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md">
      <div className="flex items-start gap-1.5 text-[11px] font-medium uppercase leading-tight tracking-wide text-gray-500 whitespace-normal">
        <span className="mt-px shrink-0 text-gray-400">{icon}</span>
        <span className="min-w-0 flex-1">{label}</span>
        {info && (
          <span className="mt-px shrink-0 cursor-help text-gray-300" title={info} aria-label={info}>
            <Info className="h-3 w-3" />
          </span>
        )}
      </div>
      <div className={`mt-2 text-2xl font-semibold tabular-nums ${success ? 'text-emerald-600' : 'text-gray-900'}`}>{wert}</div>
      {sub && <div className="mt-1 text-[11px] leading-tight text-gray-400 whitespace-normal">{sub}</div>}
    </div>
  );
}

/** Anzahlen je Prüf-Stufe (Single Source of Truth: View v_forderungs_versand_status). */
interface PruefCounts { geprueft: number; in_pruefung: number; rueckfrage: number; }
const EMPTY_PRUEF_COUNTS: PruefCounts = { geprueft: 0, in_pruefung: 0, rueckfrage: 0 };

/** Skeleton-Kachel: ruhiges Lade-Placeholder im gleichen Layout wie KpiCard. */
function KpiCardSkeleton() {
  return (
    <div className="flex h-full min-w-0 animate-pulse flex-col rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="h-3 w-2/3 rounded bg-gray-200" />
      <div className="mt-3 h-6 w-1/2 rounded bg-gray-200" />
      <div className="mt-2 h-2.5 w-3/4 rounded bg-gray-100" />
    </div>
  );
}

/**
 * Erfolgs-/Pipeline-Box: lädt die Kennzahlen-View (1 Zeile) UND die Segment-Counts
 * (v_forderungs_versand_status, mail_status='ready_for_review') als EINZIGE Quelle
 * der Wahrheit. Zeigt 8 KPI-Kacheln in EINER durchgehenden, gleichmäßig verteilten
 * Leiste. Robuste Lade-/Fehler-/Leer-Zustände: niemals dauerhaft „…".
 *
 * Die ermittelten Segment-Counts werden via `onSegmentCounts` nach oben gereicht,
 * damit die Segment-Chips dieselben (korrekten) Zahlen wie die Kacheln zeigen.
 */
function KennzahlenBox({ onSegmentCounts }: { onSegmentCounts?: (c: PruefCounts) => void }) {
  const [k, setK] = useState<VersandKennzahlen | null>(null);
  const [pruef, setPruef] = useState<PruefCounts>(EMPTY_PRUEF_COUNTS);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let ab = false;
    setState('loading');
    (async () => {
      const [kRes, sRes] = await Promise.all([
        supabase
          .from('v_forderungs_versand_kennzahlen')
          .select('geplant_anzahl,geplant_offen,versendet_anzahl,versendet_offen,zurueckgehalten_anzahl,offen_gesamt,eingegangen_gesamt')
          .maybeSingle(),
        // Segment-Counts direkt aus der View ableiten — unabhängig von der (auf 200
        // Zeilen begrenzten) Entwurfsliste. So stimmen die Zahlen mit der DB überein.
        supabase
          .from('v_forderungs_versand_status')
          .select('pruef_status')
          .eq('mail_status', 'ready_for_review'),
      ]);
      if (ab) return;

      if (kRes.error || sRes.error) {
        // Fehler nicht mehr verschlucken: sichtbar machen + Retry anbieten.
        console.error('[KennzahlenBox] Laden fehlgeschlagen:', kRes.error ?? sRes.error);
        setState('error');
        return;
      }

      setK((kRes.data as VersandKennzahlen | null) ?? null);

      const counts: PruefCounts = { geprueft: 0, in_pruefung: 0, rueckfrage: 0 };
      ((sRes.data as { pruef_status: PruefStatus | null }[] | null) ?? []).forEach(r => {
        if (r.pruef_status && r.pruef_status in counts) counts[r.pruef_status] += 1;
      });
      setPruef(counts);
      onSegmentCounts?.(counts);
      setState('ready');
    })();
    return () => { ab = true; };
    // onSegmentCounts ist stabil (useCallback im Parent); reloadKey triggert Retry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  // Gemeinsamer Grid-Wrapper für alle Zustände → KPI-Leiste über volle Breite,
  // gleichmäßige, gleich hohe Karten (bestehendes Design beibehalten).
  const Grid = ({ children }: { children: ReactNode }) => (
    <div className="w-full max-w-none">
      {/* Responsives, umbrechendes Raster: auto-fit + minmax sorgt dafür, dass bei
          JEDER Breite/Zoomstufe alle Kacheln vollständig sichtbar sind (Umbruch in
          mehrere Zeilen statt horizontalem Scrollen/Abschneiden). */}
      <div
        className="grid w-full auto-rows-fr gap-3"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}
      >
        {children}
      </div>
    </div>
  );

  if (state === 'loading') {
    return <Grid>{Array.from({ length: 8 }).map((_, i) => <KpiCardSkeleton key={i} />)}</Grid>;
  }

  if (state === 'error') {
    return (
      <div className="w-full max-w-none">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <span className="inline-flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            Kennzahlen konnten nicht geladen werden.
          </span>
          <button
            type="button"
            onClick={() => setReloadKey(x => x + 1)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-white px-2.5 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Neu laden
          </button>
        </div>
      </div>
    );
  }

  return (
    <Grid>
      <KpiCard
        success
        icon={<ShieldCheck className="w-4 h-4" />}
        label="Geprüft"
        wert={intDe(pruef.geprueft)}
        sub="geht automatisch raus"
        info="Per Bilderkennung bestätigt – geht automatisch raus."
      />
      <KpiCard
        icon={<ScanSearch className="w-4 h-4" />}
        label="In Prüfung"
        wert={intDe(pruef.in_pruefung)}
        sub="Bilderkennung läuft"
        info="Rechnung wird noch per Bilderkennung geprüft."
      />
      <KpiCard
        icon={<HelpCircle className="w-4 h-4" />}
        label="Rückfrage"
        wert={intDe(pruef.rueckfrage)}
        sub="bitte prüfen/freigeben"
        info="Bitte manuell prüfen/freigeben."
      />
      <KpiCard
        icon={<Hourglass className="w-4 h-4" />}
        label="In der Pipeline"
        wert={`${intDe(k?.geplant_anzahl)} Mails`}
        sub={`Betrag ${eur(k?.geplant_offen)}`}
        info="Geplant für den automatischen Versand (Mo–Fr, gedrosselt)."
      />
      <KpiCard
        icon={<CheckCircle2 className="w-4 h-4" />}
        label="Versendet"
        wert={`${intDe(k?.versendet_anzahl)} Mails`}
        sub={eur(k?.versendet_offen)}
        info="Bereits versendete Mahnungen."
      />
      <KpiCard
        icon={<PauseCircle className="w-4 h-4" />}
        label="Zurückgehalten"
        wert={intDe(k?.zurueckgehalten_anzahl)}
        sub="manuell prüfen"
        info="Sicherheitshalber gestoppt (z. B. bezahlt, Empfänger fehlt) – manuell prüfen."
      />
      <KpiCard
        icon={<BarChart3 className="w-4 h-4" />}
        label="Erwartet"
        wert={eur(k?.offen_gesamt)}
        sub="alle Forderungen in den Mails"
        info="Summe aller offenen Forderungen im Mahnlauf."
      />
      <KpiCard
        success
        icon={<PiggyBank className="w-4 h-4" />}
        label="Eingegangen"
        wert={eur(k?.eingegangen_gesamt)}
        sub="durch Forderungsmanager"
        info="Zahlungseingänge nach Versand (sobald Bankabgleich aktiv)."
      />
    </Grid>
  );
}

/** Live-Countdown bis zur geplanten Versandzeit (Update minutengenau bzw. <1 Std feiner). */
function CountdownBadge({ versandAt }: { versandAt: Date | null }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!versandAt) return;
    // Update alle 30 s, damit der Countdown spürbar läuft (Minuten-genau gerundet).
    const id = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, [versandAt]);

  if (!versandAt) return null;
  const diffMs = versandAt.getTime() - now;
  if (diffMs <= 0) {
    return (
      <span className="inline-flex items-center gap-1 font-medium text-emerald-700 bg-emerald-50 rounded px-1.5 py-0.5">
        <Send className="w-3 h-3" /> steht zum Versand bereit
      </span>
    );
  }
  const totalMin = Math.floor(diffMs / 60_000);
  const days = Math.floor(totalMin / 1440);
  const hours = Math.floor((totalMin % 1440) / 60);
  const mins = totalMin % 60;
  let label: string;
  if (days > 0) label = `${days} ${days === 1 ? 'Tag' : 'Tage'} ${hours} Std`;
  else if (hours > 0) label = `${hours} Std ${mins} Min`;
  else label = `${mins} Min`;
  return (
    <span className="inline-flex items-center gap-1 font-medium text-amber-700 bg-amber-50 rounded px-1.5 py-0.5">
      <Hourglass className="w-3 h-3" /> geht raus in {label}
    </span>
  );
}

/** Schreibgeschützte, „eingefrorene" Detailansicht für den Auto-Versand-Tab. */
function FrozenMailPreview({ d }: { d: Draft }) {
  return (
    <div className="px-4 py-4 border-t border-gray-100 bg-gray-100 space-y-3">
      <div className="mx-auto max-w-[640px] space-y-3">
        {/* Empfänger + CC */}
        <div className="bg-white rounded-lg border border-gray-200 px-4 py-2.5 text-[12px] text-gray-600 space-y-1">
          <div><span className="inline-block w-14 text-gray-400 font-medium">An</span>{d.recipient || '—'}</div>
          {(d.cc ?? []).length > 0 && <div><span className="inline-block w-14 text-gray-400 font-medium">CC</span>{(d.cc ?? []).join(', ')}</div>}
          <div><span className="inline-block w-14 text-gray-400 font-medium">Betreff</span><span className="font-semibold text-gray-800">{d.subject}</span></div>
        </div>

        {/* Gerenderter E-Mail-Text (read-only) */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-3 py-1.5 border-b border-gray-100 bg-gray-50 text-[11px] font-medium text-gray-400 flex items-center gap-1">
            <Lock className="w-3 h-3" /> Inhalt (eingefroren, nur Ansicht)
          </div>
          {d.body_html ? (
            <iframe
              title={`Vorschau: ${d.subject}`}
              sandbox="allow-same-origin"
              referrerPolicy="no-referrer"
              srcDoc={d.body_html}
              className="w-full bg-white"
              style={{ height: 420, border: 0 }}
            />
          ) : (
            <pre className="px-4 py-3 text-[12px] text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">{d.body_text || '— kein Inhalt —'}</pre>
          )}
        </div>

        {/* Anhänge — nur Anzeige (keine Aktionen) */}
        {(d.attachments ?? []).length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
            <div className="text-[11px] font-medium text-gray-400 mb-1.5 flex items-center gap-1"><Paperclip className="w-3 h-3" /> Anhänge ({(d.attachments ?? []).length})</div>
            <div className="flex flex-wrap gap-1.5">
              {(d.attachments ?? []).map(a => (
                <span key={a.id} className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 rounded px-1.5 py-0.5 text-[11px] max-w-[260px]">
                  <FileText className="w-3 h-3 flex-shrink-0" /><span className="truncate">{a.filename}</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      <p className="mx-auto max-w-[640px] text-[10px] text-gray-400 text-center">Diese E-Mail geht automatisch raus — Ansicht ist schreibgeschützt.</p>
    </div>
  );
}

type Tab = 'inbox' | 'ready_for_review' | 'auto' | 'draft' | 'sent' | 'all';
const TABS: { key: Tab; label: string }[] = [
  { key: 'inbox', label: 'Posteingang' },
  { key: 'ready_for_review', label: 'Zur Freigabe' }, { key: 'auto', label: 'Auto-Versand' },
  { key: 'draft', label: 'Entwürfe' },
  { key: 'sent', label: 'Gesendet' }, { key: 'all', label: 'Alle' },
];

export default function EntwuerfeFreigabePage() {
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [markenByBU, setMarkenByBU] = useState<Record<string, MarkeInfo>>({});
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [uploadBusyId, setUploadBusyId] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('ready_for_review');
  const [q, setQ] = useState('');
  const [prioFilter, setPrioFilter] = useState<'alle' | Prioritaet>('alle');
  const [fristFilter, setFristFilter] = useState<FristFilter>('alle');
  const [sortMode, setSortMode] = useState<SortMode>('neueste');
  // Auto-Versand-Zusatzfilter (nur im 'auto'-Tab sichtbar)
  const [themaFilter, setThemaFilter] = useState('alle');
  const [firmaFilter, setFirmaFilter] = useState('alle');
  const [postfachFilter, setPostfachFilter] = useState('alle');
  // Sortierung im Auto-Versand-Tab (Default: Versandzeit aufsteigend = nächster Versand zuerst).
  const [autoSort, setAutoSort] = useState<'versandzeit' | 'firma' | 'prioritaet'>('versandzeit');
  // Prüf-Status-Map (draft_id → PruefInfo) + Segment-Auswahl im Auto-Tab (Default „Geprüft").
  const [pruefMap, setPruefMap] = useState<Record<string, PruefInfo>>({});
  const [pruefSegment, setPruefSegment] = useState<PruefStatus>('geprueft');
  const [lightbox, setLightbox] = useState<{ url: string; filename: string } | null>(null);
  const [pickerDraft, setPickerDraft] = useState<Draft | null>(null);
  // Posteingang: eingehende Mails des Postfachs forderungen@ direkt aus email_archive.
  // (Typ InboxMail liegt auf Modulebene, gemeinsam mit InboxCard + Klassifizierungs-Helfern.)
  const [inbox, setInbox] = useState<InboxMail[]>([]);
  const [inboxLoading, setInboxLoading] = useState(false);
  const [openMail, setOpenMail] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    // Posteingang-Tab lädt seine Daten separat (loadInbox) aus email_archive –
    // hier keine email_drafts-Query mit status='inbox' absetzen.
    if (tab === 'inbox') { setDrafts([]); setLoading(false); return; }
    // Marken-Map (business_unit → vollständige Markendaten für Absender + Vorschau)
    const { data: marken } = await supabase
      .from('marken')
      .select('marken_name, rechtstraeger, business_unit, absender_email, email, anschrift, telefon, website, logo_url, footer_disclaimer');
    const buMarke: Record<string, MarkeInfo> = {};
    (marken ?? []).forEach((m: any) => {
      if (!m.business_unit) return;
      const brand = markeToEmailBrand(m as MarkeRowForBrand);
      buMarke[String(m.business_unit).toLowerCase()] = {
        name: m.marken_name,
        email: m.absender_email || m.email || '',
        color: brand.primaryColor,
        brand,
      };
    });
    setMarkenByBU(buMarke);

    let query = supabase.from('email_drafts')
      .select('id,recipient,cc,bcc,subject,body_html,body_text,status,review_note,created_by_agent_id,akte_id,created_at,marken_name,from_email,frist_versand,prioritaet')
      .order('created_at', { ascending: false }).limit(200);
    // Auto-Versand-Tab: ready_for_review UND draft, damit auch „in Prüfung" und
    // „Rückfrage" sichtbar sind. Andere Tabs filtern weiter auf ihren Tab-Status.
    if (tab === 'auto') {
      query = query.in('status', ['ready_for_review', 'draft']);
      // SICHERHEIT: serverseitig auf echte Forderungs-Mahnungen beschränken (Allowlist).
      query = query.in('created_by_agent_id', AUTO_AGENTS as unknown as string[]);
    } else if (tab !== 'all') {
      query = query.eq('status', tab);
    }
    const { data, error } = await query;
    if (error) { toast.error('Laden fehlgeschlagen: ' + error.message); setLoading(false); return; }
    const rows = (data as Draft[]) ?? [];
    const akteIds = [...new Set(rows.map(r => r.akte_id).filter(Boolean))] as string[];
    const draftIds = rows.map(r => r.id);
    const [akten, atts] = await Promise.all([
      akteIds.length ? supabase.from('akten').select('id,business_unit,akten_nummer').in('id', akteIds) : Promise.resolve({ data: [] as any[] }),
      draftIds.length ? supabase.from('email_draft_attachments').select('id,draft_id,source_type,dokument_id,storage_path,drive_file_id,filename,mime_type').in('draft_id', draftIds) : Promise.resolve({ data: [] as any[] }),
    ]);
    const buMap = new Map((akten.data ?? []).map((a: any) => [a.id, a.business_unit]));
    const azMap = new Map((akten.data ?? []).map((a: any) => [a.id, a.akten_nummer]));
    const attMap = new Map<string, Attachment[]>();
    (atts.data ?? []).forEach((a: any) => { const arr = attMap.get(a.draft_id) ?? []; arr.push(a); attMap.set(a.draft_id, arr); });
    setDrafts(rows.map(r => ({ ...r, business_unit: r.akte_id ? buMap.get(r.akte_id) ?? null : null, aktenzeichen: r.akte_id ? azMap.get(r.akte_id) ?? null : null, attachments: attMap.get(r.id) ?? [] })));

    // Auto-Versand-Tab: Prüf-Status der Bilderkennung je Entwurf laden (eine Zeile/Entwurf).
    if (tab === 'auto' && draftIds.length) {
      const { data: vs } = await supabase
        .from('v_forderungs_versand_status')
        .select('draft_id,pruef_status,befund,haupt_datei,haupt_betrag')
        .in('draft_id', draftIds);
      const map: Record<string, PruefInfo> = {};
      (vs as VersandStatusRow[] | null ?? []).forEach(r => {
        if (!r.draft_id || !r.pruef_status) return;
        map[r.draft_id] = {
          pruef_status: r.pruef_status,
          befund: r.befund ?? null,
          haupt_datei: r.haupt_datei ?? null,
          haupt_betrag: r.haupt_betrag ?? null,
        };
      });
      setPruefMap(map);
    } else {
      setPruefMap({});
    }
    setLoading(false);
  }, [tab]);

  useEffect(() => { load(); }, [load]);
  const loadInbox = useCallback(async () => {
    setInboxLoading(true);
    const { data, error } = await supabase
      .from('email_archive')
      .select('id,gmail_message_id,from_email,from_name,subject,snippet,body_text,body_html,date,is_read,has_attachments,attachment_count,attachments')
      .eq('user_id', 'forderungen_info')
      .neq('from_email', 'forderungen@gutachter-koop.de')
      .order('date', { ascending: false })
      .limit(200);
    if (!error && data) setInbox(data as InboxMail[]);
    setInboxLoading(false);
  }, []);
  useEffect(() => { if (tab === 'inbox') loadInbox(); }, [tab, loadInbox]);
  useEffect(() => { const id = new URLSearchParams(window.location.search).get('draft'); if (id) setOpenId(id); }, []);

  const patchDraft = (id: string, patch: Partial<Draft>) => setDrafts(prev => prev.map(x => x.id === id ? { ...x, ...patch } : x));

  // Markenwahl am Absender → persistiert marken_name + from_email am Entwurf.
  // marken_name gewinnt beim Versand über die aus business_unit abgeleitete Marke.
  const changeBrand = async (d: Draft, markenName: string, fromEmail: string) => {
    patchDraft(d.id, { marken_name: markenName }); // optimistisch (Vorschau/Anzeige)
    const patch: { marken_name: string; from_email?: string } = { marken_name: markenName };
    if (fromEmail) patch.from_email = fromEmail;
    const { error } = await supabase.from('email_drafts').update(patch).eq('id', d.id);
    if (error) { toast.error('Marke konnte nicht gespeichert werden: ' + error.message); load(); return; }
    toast.success(`Absender: ${markenName}`);
  };

  const preview = async (a: Attachment) => {
    const url = await getViewUrl(a);
    if (!url) { toast.error('Vorschau nicht verfügbar'); return; }
    setLightbox({ url, filename: a.filename });
  };

  const addAttachment = async (d: Draft, dok: DocRow) => {
    try {
      const { error } = await supabase.from('email_draft_attachments').insert({
        draft_id: d.id, source_type: 'supabase', dokument_id: dok.id, filename: dok.dateiname, mime_type: dok.mime_type,
      });
      if (error) throw error;
      toast.success('Anhang hinzugefügt'); setPickerDraft(null); load();
    } catch (e: any) { toast.error('Anhang fehlgeschlagen: ' + (e?.message ?? e)); }
  };

  // ── Upload per Datei-Auswahl / Drag&Drop ──────────────────────────────────
  // Datei → Storage 'dokumente' → dokumente-Eintrag (akte_id nur wenn inAkte →
  // liegt damit in der Akte) → email_draft_attachments-Eintrag (source 'supabase').
  // Mehrere Dateien werden nacheinander hochgeladen; Fehler je Datei via toast.
  const uploadAttachments = async (d: Draft, files: File[], inAkte: boolean) => {
    if (!files.length) return;
    setUploadBusyId(d.id);
    let ok = 0;
    try {
      for (const file of files) {
        try {
          const safe = safeFileName(file.name);
          const ablegen = inAkte && !!d.akte_id;
          const folder = ablegen ? d.akte_id! : 'ohne-akte';
          const path = `email-anhang/${folder}/${Date.now()}-${safe}`;
          const contentType = file.type || 'application/octet-stream';

          const up = await supabase.storage.from('dokumente').upload(path, file, { contentType, upsert: false });
          if (up.error) {
            // RLS oder sonstiger Storage-Fehler explizit melden (kein stilles Scheitern).
            toast.error(`Upload „${file.name}" fehlgeschlagen: ${up.error.message}`);
            continue;
          }

          const { data: dok, error: dokErr } = await supabase.from('dokumente').insert({
            akte_id: ablegen ? d.akte_id : null,
            dateiname: file.name,
            dateipfad: path,
            mime_type: contentType,
            kategorie: 'email_anhang',
            dateigroesse: file.size ?? null,
          }).select('id').single();
          if (dokErr) {
            toast.error(`„${file.name}": Dokument-Eintrag fehlgeschlagen: ${dokErr.message}`);
            continue;
          }

          const { error: attErr } = await supabase.from('email_draft_attachments').insert({
            draft_id: d.id,
            source_type: 'supabase',
            dokument_id: (dok as any).id,
            filename: file.name,
            mime_type: contentType,
            size_bytes: file.size ?? null,
          });
          if (attErr) {
            toast.error(`„${file.name}": Anhang-Verknüpfung fehlgeschlagen: ${attErr.message}`);
            continue;
          }
          ok += 1;
        } catch (e: any) {
          toast.error(`„${file.name}" fehlgeschlagen: ${e?.message ?? e}`);
        }
      }
      if (ok > 0) {
        toast.success(
          inAkte && d.akte_id
            ? `${ok} ${ok === 1 ? 'Datei' : 'Dateien'} angehängt & in der Akte abgelegt.`
            : `${ok} ${ok === 1 ? 'Datei' : 'Dateien'} angehängt.`,
        );
        load();
      }
    } finally {
      setUploadBusyId(null);
    }
  };

  const renameAttachment = async (a: Attachment) => {
    const name = window.prompt('Neuer Dateiname:', a.filename);
    if (!name || !name.trim() || name.trim() === a.filename) return;
    const { error } = await supabase.from('email_draft_attachments').update({ filename: name.trim() }).eq('id', a.id);
    if (error) { toast.error('Umbenennen fehlgeschlagen: ' + error.message); return; }
    toast.success('Anhang umbenannt'); load();
  };

  const deleteAttachment = async (a: Attachment) => {
    if (!window.confirm(`Anhang „${a.filename}" entfernen?`)) return;
    const { error } = await supabase.from('email_draft_attachments').delete().eq('id', a.id);
    if (error) { toast.error('Entfernen fehlgeschlagen: ' + error.message); return; }
    toast.success('Anhang entfernt'); load();
  };

  const freigeben = async (d: Draft) => {
    if (recipientInvalid(d.recipient)) { toast.error('Empfänger ungültig — bitte zuerst bearbeiten.'); return; }
    setBusyId(d.id);
    try {
      const { data: u } = await supabase.auth.getUser();
      const userId = u?.user?.id;
      if (!userId) { toast.error('Nicht eingeloggt'); return; }
      const res = await fetch(`/api/email-draft-send`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: d.id, userId }) });
      const raw = await res.text(); let json: any = {}; try { json = raw ? JSON.parse(raw) : {}; } catch { /* */ }
      if (!res.ok || json.ok === false) throw new Error(json.error || `Senden fehlgeschlagen (HTTP ${res.status})`);
      toast.success('Gesendet an ' + d.recipient);
      setDrafts(prev => prev.filter(x => x.id !== d.id));
    } catch (e: any) { toast.error(String(e?.message ?? e)); }
    finally { setBusyId(null); }
  };

  const verwerfen = async (d: Draft) => {
    if (!window.confirm(`Entwurf „${d.subject}" verwerfen?`)) return;
    setBusyId(d.id);
    try {
      const { error } = await supabase.from('email_drafts').update({ status: 'undone' }).eq('id', d.id);
      if (error) throw error;
      toast.success('Entwurf verworfen'); setDrafts(prev => prev.filter(x => x.id !== d.id));
    } catch (e: any) { toast.error('Verwerfen fehlgeschlagen: ' + (e?.message ?? e)); }
    finally { setBusyId(null); }
  };

  const isAuto = tab === 'auto';

  /** Effektive Prüf-Stufe eines Entwurfs (fehlende View-Zeile → „in Prüfung"). */
  const pruefStatusOf = useCallback(
    (id: string): PruefStatus => pruefMap[id]?.pruef_status ?? 'in_pruefung',
    [pruefMap],
  );

  // Anzahlen je Prüf-Stufe — Single Source of Truth ist die View
  // v_forderungs_versand_status (mail_status='ready_for_review'). Die Counts werden
  // von der KennzahlenBox geladen und über onSegmentCounts hierher hochgereicht, damit
  // Segment-Chips und KPI-Kacheln IDENTISCHE, DB-genaue Zahlen zeigen (nicht aus der
  // auf 200 Zeilen begrenzten Entwurfsliste abgeleitet).
  const [pruefCounts, setPruefCounts] = useState<PruefCounts>(EMPTY_PRUEF_COUNTS);
  const handleSegmentCounts = useCallback((c: PruefCounts) => setPruefCounts(c), []);

  // Versand-Plan (gedrosselt) — nur im Auto-Versand-Tab relevant.
  const versandPlan = useMemo(
    () => (isAuto ? buildVersandPlan(drafts) : new Map<string, Date>()),
    [isAuto, drafts],
  );

  // Postfach-Optionen: dynamisch aus den Daten + Standard-Postfächer.
  const postfaecher = useMemo(() => {
    const set = new Set<string>(['forderungen@gutachter-koop.de', 'info@gutachter-koop.de', 'info@lenneglas.de']);
    drafts.forEach(d => set.add(postfachOf(d)));
    return Array.from(set).filter(x => x && x !== '—').sort();
  }, [drafts]);

  const term = q.trim().toLowerCase();
  const filtered = useMemo(() => {
    let list = term
      ? drafts.filter(d => `${d.subject} ${d.recipient} ${d.review_note ?? ''}`.toLowerCase().includes(term))
      : drafts.slice();
    if (prioFilter !== 'alle') list = list.filter(d => d.prioritaet === prioFilter);
    if (fristFilter !== 'alle') list = list.filter(d => matchesFristFilter(fristStatus(d.frist_versand), fristFilter));
    if (isAuto) {
      // SICHERHEITS-SCHUTZFILTER (clientseitig, zusätzlich zur Query): nur echte
      // Forderungs-Mahnungen. So bleiben Stellungnahmen/Verträge/Sonstige garantiert
      // aus Liste, Countdown und Zähler des Auto-Tabs draußen.
      list = list.filter(d => isAutoVersandAgent(d.created_by_agent_id));
      // Segmentierte Unteransicht: nur Entwürfe der gewählten Prüf-Stufe.
      list = list.filter(d => pruefStatusOf(d.id) === pruefSegment);
      // Zusatzfilter Thema / Firma / Postfach
      if (themaFilter !== 'alle') list = list.filter(d => themaOf(d.created_by_agent_id).key === themaFilter);
      if (firmaFilter !== 'alle') list = list.filter(d => firmaOf(d) === firmaFilter);
      if (postfachFilter !== 'alle') list = list.filter(d => postfachOf(d) === postfachFilter);
      // Sortierung im Auto-Versand-Tab.
      const prioRank = (p?: string | null) => ({ dringend: 0, hoch: 1, mittel: 2, niedrig: 3 } as Record<string, number>)[p ?? ''] ?? 2;
      const byVersand = (a: Draft, b: Draft) => (versandPlan.get(a.id)?.getTime() ?? 0) - (versandPlan.get(b.id)?.getTime() ?? 0);
      if (autoSort === 'firma') {
        list.sort((a, b) => firmaOf(a).localeCompare(firmaOf(b), 'de') || byVersand(a, b));
      } else if (autoSort === 'prioritaet') {
        list.sort((a, b) => prioRank(a.prioritaet) - prioRank(b.prioritaet) || byVersand(a, b));
      } else {
        // Default: nächster Versand zuerst (Versandzeit aufsteigend).
        list.sort(byVersand);
      }
    } else {
      list.sort((a, b) => compareDrafts(a, b, sortMode));
    }
    return list;
  }, [drafts, term, prioFilter, fristFilter, sortMode, isAuto, themaFilter, firmaFilter, postfachFilter, versandPlan, autoSort, pruefSegment, pruefStatusOf]);
  const readyCount = drafts.filter(d => d.status === 'ready_for_review').length;

  const selectCls = 'text-xs font-medium text-gray-700 border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#9B1C1C]/20';

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#9B1C1C]/10 flex items-center justify-center"><Mail className="w-5 h-5 text-[#9B1C1C]" /></div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">E-Mail-Freigaben</h1>
            <p className="text-sm text-gray-500">{readyCount > 0 ? `${readyCount} zur Freigabe` : 'Keine offenen Freigaben'}</p>
          </div>
        </div>
        <button onClick={load} className="flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 px-3 py-2 rounded-lg hover:bg-gray-100"><RefreshCw className="w-4 h-4" /> Aktualisieren</button>
      </div>

      <div className="flex items-center gap-1 border-b border-gray-200">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.key ? 'border-[#9B1C1C] text-[#9B1C1C]' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>{t.label}</button>
        ))}
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Suchen (Betreff, Empfänger, Kontext)…" className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#9B1C1C]/20" />
      </div>

      {/* Filter-/Sortierleiste — minimalistisch, gleiche shadcn-/Tailwind-Stile */}
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <span className="inline-flex items-center gap-1 text-gray-400 font-medium">
          <Flag className="w-3.5 h-3.5" /> Dringlichkeit
        </span>
        <select value={prioFilter} onChange={e => setPrioFilter(e.target.value as 'alle' | Prioritaet)} aria-label="Filter Dringlichkeit" className={selectCls}>
          <option value="alle">Alle</option>
          <option value="niedrig">Niedrig</option>
          <option value="mittel">Mittel</option>
          <option value="hoch">Hoch</option>
          <option value="dringend">Dringend</option>
        </select>

        <span className="inline-flex items-center gap-1 text-gray-400 font-medium ml-1">
          <CalendarClock className="w-3.5 h-3.5" /> Frist
        </span>
        <select value={fristFilter} onChange={e => setFristFilter(e.target.value as FristFilter)} aria-label="Filter Frist" className={selectCls}>
          <option value="alle">Alle</option>
          <option value="ueberfaellig">Überfällig</option>
          <option value="heute">Heute</option>
          <option value="woche">Diese Woche</option>
          <option value="ohne">Ohne Frist</option>
        </select>

        <span className="inline-flex items-center gap-1 text-gray-400 font-medium ml-auto">
          <ArrowDownUp className="w-3.5 h-3.5" /> Sortierung
        </span>
        <select value={sortMode} onChange={e => setSortMode(e.target.value as SortMode)} aria-label="Sortierung" className={selectCls} disabled={isAuto} title={isAuto ? 'Im Auto-Versand wird nach Versandzeit sortiert' : undefined}>
          <option value="neueste">Neueste</option>
          <option value="frist">Frist (früheste zuerst)</option>
        </select>
      </div>

      {/* Auto-Versand: Erfolgs-/Pipeline-Box, Sortierung, Zusatzfilter + Hinweis */}
      {isAuto && (
        <>
          {/* KPI-Leiste bleibt innerhalb des normalen Inhaltsbereichs (kein Full-Bleed
              mehr) und bricht über das responsive Raster sauber in mehrere Zeilen um. */}
          <div className="w-full">
            <KennzahlenBox onSegmentCounts={handleSegmentCounts} />
          </div>

          {/* Segmentierte Unteransicht: Prüf-Stufen (Default „Geprüft") */}
          <div className="inline-flex items-center gap-1 rounded-xl border border-gray-200 bg-gray-50 p-1">
            {PRUEF_SEGMENTS.map(({ key, icon }) => {
              const active = pruefSegment === key;
              const count = pruefCounts[key];
              const meta = PRUEF_META[key];
              return (
                <button
                  key={key}
                  onClick={() => setPruefSegment(key)}
                  aria-pressed={active}
                  title={meta.hint}
                  className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                    active ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'
                  }`}
                >
                  <span className={active ? '' : 'text-gray-400'}>{icon}</span>
                  {meta.label}
                  <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold ${active ? meta.badgeClass : 'bg-gray-200 text-gray-500'}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-gray-400 -mt-2">{PRUEF_META[pruefSegment].hint}.</p>

          <div className="flex items-center gap-2 flex-wrap text-xs">
            <span className="inline-flex items-center gap-1 text-gray-400 font-medium">
              <ArrowDownUp className="w-3.5 h-3.5" /> Sortierung
            </span>
            <select value={autoSort} onChange={e => setAutoSort(e.target.value as 'versandzeit' | 'firma' | 'prioritaet')} aria-label="Sortierung Auto-Versand" className={selectCls}>
              <option value="versandzeit">Versandzeit (Standard)</option>
              <option value="firma">Betrieb/Firma</option>
              <option value="prioritaet">Priorität</option>
            </select>
          </div>

          <div className="flex items-center gap-2 flex-wrap text-xs">
            <span className="inline-flex items-center gap-1 text-gray-400 font-medium">
              <Filter className="w-3.5 h-3.5" /> Thema
            </span>
            <select value={themaFilter} onChange={e => setThemaFilter(e.target.value)} aria-label="Filter Thema" className={selectCls}>
              {THEMA_FILTER.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>

            <span className="inline-flex items-center gap-1 text-gray-400 font-medium ml-1">
              <Building2 className="w-3.5 h-3.5" /> Firma
            </span>
            <select value={firmaFilter} onChange={e => setFirmaFilter(e.target.value)} aria-label="Filter Firma" className={selectCls}>
              <option value="alle">Alle Firmen</option>
              <option value="SV Büro">SV Büro</option>
              <option value="LCS">LCS</option>
              <option value="Sonstige">Sonstige</option>
            </select>

            <span className="inline-flex items-center gap-1 text-gray-400 font-medium ml-1">
              <Mailbox className="w-3.5 h-3.5" /> Postfach
            </span>
            <select value={postfachFilter} onChange={e => setPostfachFilter(e.target.value)} aria-label="Filter Postfach" className={selectCls}>
              <option value="alle">Alle Postfächer</option>
              {postfaecher.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>

          <div className="flex items-start gap-2 text-[12px] rounded-lg border border-gray-100 bg-gray-50 text-gray-600 px-3 py-2">
            <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-[#9B1C1C]" />
            <span>Diese E-Mails gehen automatisch &amp; gedrosselt raus (Mo–Fr). Unsichere (z.&nbsp;B. fehlender Empfänger) liegen unter „Entwürfe" mit Hinweis „MANUELL PRÜFEN".</span>
          </div>

          <p className="text-[11px] text-gray-400 leading-relaxed">
            Nur kritisch geprüfte Forderungen, die zu 100&nbsp;% automatisch rausgehen.
            Stellungnahmen, Verträge &amp; alles andere bleiben unter „Zur Freigabe" (manuell).
          </p>
        </>
      )}

      {tab === 'inbox' ? (
        inboxLoading ? (
          <div className="space-y-3">{[1, 2, 3].map(i => <div key={i} className="h-24 bg-gray-100 rounded-xl animate-pulse" />)}</div>
        ) : inbox.length === 0 ? (
          <div className="py-20 text-center"><Inbox className="w-12 h-12 text-gray-200 mx-auto mb-4" /><p className="text-base font-semibold text-gray-400">Keine eingehenden Forderungs-Mails.</p></div>
        ) : (
          <div className="space-y-3">
            {inbox.map(m => <InboxCard key={m.id} m={m} open={openMail === m.id} onToggle={() => setOpenMail(prev => prev === m.id ? null : m.id)} />)}
          </div>
        )
      ) : loading ? (
        <div className="space-y-3">{[1, 2, 3].map(i => <div key={i} className="h-28 bg-gray-100 rounded-xl animate-pulse" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="py-20 text-center"><Inbox className="w-12 h-12 text-gray-200 mx-auto mb-4" /><p className="text-base font-semibold text-gray-400">Keine Einträge</p></div>
      ) : (
        <div className="space-y-3">
          {filtered.map(d => (
            <DraftCard key={d.id} d={d} marke={d.business_unit ? markenByBU[d.business_unit] ?? null : null} brandByBU={markenByBU} busy={busyId === d.id} open={openId === d.id}
              onToggle={() => setOpenId(openId === d.id ? null : d.id)} onSend={() => freigeben(d)} onDiscard={() => verwerfen(d)}
              onSaved={p => patchDraft(d.id, p)} onPreview={preview} onAddAttachment={setPickerDraft}
              onRenameAttachment={renameAttachment} onDeleteAttachment={deleteAttachment} onBrandChange={changeBrand}
              recipientSuggestions={crmRecipientSuggestions}
              versandAt={isAuto ? (versandPlan.get(d.id) ?? null) : undefined} frozen={isAuto}
              pruefInfo={isAuto ? (pruefMap[d.id] ?? { pruef_status: 'in_pruefung', befund: null, haupt_datei: null, haupt_betrag: null }) : null}
              onRequestEdit={() => setTab('draft')} />
          ))}
        </div>
      )}

      {lightbox && <Lightbox url={lightbox.url} filename={lightbox.filename} onClose={() => setLightbox(null)} />}
      {pickerDraft && (
        <AttachmentDialog
          draft={pickerDraft}
          uploadBusy={uploadBusyId === pickerDraft.id}
          onClose={() => setPickerDraft(null)}
          onPick={(dok) => addAttachment(pickerDraft, dok)}
          onUpload={(files, inAkte) => uploadAttachments(pickerDraft, files, inAkte)}
        />
      )}
    </div>
  );
}
